import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { countries } from "../countries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ArrowLeft,
  Sparkles,
  SquareArrowOutUpRight,
  Copy,
  Mail,
  Loader,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronsUpDown,
  TriangleAlert,
  Check,
  Info,
  CircleCheck,
  X,
  BadgeCheck,
  CircleDashed,
  CircleX,
  FileText,
  RefreshCw,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Icon } from "@iconify/react";
import { canonicalizeDomain } from "@/utils/domain";
import aiModeIcon from "@/assets/ai-mode.svg";
import grokIcon from "@/assets/grok.svg";
import copilotIcon from "@/assets/copilot.svg";
import { Skeleton } from "@/components/ui/skeleton";
import {
  capturePosthogEvent,
  extractDomain,
  toDomainType,
  toPlatformValue,
} from "@/lib/posthog";
import {
  getDomainTypeBadgeClassName,
  getUrlTypeBadgeClassName,
  toUrlTypeLabel,
} from "@/lib/urlTypes";
import { visabilityAPI } from "./api";
import registrationIcon from "../assets/RegistrationIcon.svg";
import brandRegisterIcon from "../assets/BrandRegisterIcon.svg";
import clusterRegisterIcon from "../assets/ClusterRegisterIcon.svg";
import promptRegisterIcon from "../assets/PromptRegisterIcon.svg";
import {
  CheckCheck,
  CircleDotDashed,
  CircleFadingPlus,
  Focus,
  FolderTree,
  Globe,
  MapPin,
  MessageSquareQuoteIcon,
  MoveRight,
  Waypoints,
} from "lucide-react";
import type { Brand } from "@/components/AppSidebar";

export type Prompt = {
  country: string;
  text: string;
  is_active: boolean;
  id: string;
  brand: string;
  cluster?: string;
  created_at: Date;
  updated_at: Date;
  isOwnBrand?: boolean;
  visibility?: number | null;
  position?: number | null;
  positionsByRun?: {
    runId: string;
    positions: Record<string, number>;
    isMainMentioned?: boolean;
  }[];
};

export type PartialData = {
  domain: string;
  url: string;
  type: string;
  used: number;
  usedPercents: string;
  promptId?: string;
  domainType?: string;
  platforms?: string;
  providers?: string;
  provider?: string;
  typeDomain: string;
};

export type Domains = {
  id?: string;
  rowNumber?: number;
  brandId?: string;
  runDate?: string;
  createdAt?: string;
  domain: string;
  used: number;
  url?: string;
  type?: string;
  domainType?: string;
  updatedAt?: string | null;
  isSnapshotStale?: boolean;
  isSnapshotPending?: boolean;
  isManualRefreshRateLimited?: boolean;
  manualRefreshAvailableAt?: string | null;
  snapshotDetails?: {
    finalUrl?: string | null;
    normalizedUrl?: string | null;
    statusCode?: number | null;
    title?: string | null;
    metaDescription?: string | null;
    h1?: string | null;
    topHeadings?: string[];
    excerpt?: string | null;
  } | null;
  youtubeDetails?: {
    url?: string | null;
    originalUrl?: string | null;
    videoId?: string | null;
    title?: string | null;
    description?: string | null;
    channelName?: string | null;
    publishedAt?: string | null;
    fetchedAt?: string | null;
    status?: string | null;
    errorReason?: string | null;
    youtubeFlags?: string[];
  } | null;
  promptId?: string | null;
  mentionedBrands?: { id: string; url?: string; name?: string; key?: string }[];
  isLoadingRow?: boolean;
  provider?: string;
  providers?: string[];
  platforms?: string[];
  status?: string;
};

export const statusOptions = (
  needsAction: string,
  progress: string,
  resolved: string,
  canceled?: string,
) =>
  [
    {
      value: "toAddress",
      label: "Needs Attention",
      icon: <AlertCircle className={`text-[${needsAction}] w-4 h-4`} />,
    },
    {
      value: "inProgress",
      label: "In Progress",
      icon: <Loader className={`text-[${progress}] w-4 h-4`} />,
    },
    {
      value: "resolved",
      label: "Completed",
      icon: <CheckCircle className={`text-[${resolved}] w-4 h-4`} />,
    },
    canceled
      ? {
          value: "canceled",
          label: "Canceled",
          icon: <CircleX className={`text-[${canceled}] w-4 h-4`} />,
        }
      : null,
    {
      value: "default",
      label: "Change Status",
      icon: <CircleDashed className="text-white w-4 h-4" />,
    },
  ].filter(Boolean) as {
    value: string;
    label: string;
    icon: React.JSX.Element;
  }[];

export const promptStatus = [
  {
    value: "active",
    label: "Active",
    icon: <CircleCheck className="text-green-500 w-4 h-4" />,
  },
  {
    value: "inactive",
    label: "Inactive",
    icon: <AlertCircle className="text-[#DC3E42] w-4 h-4" />,
  },
];

function getTimeDifferenceFromNow(dateInput: Date | string | number) {
  const now = new Date();
  const target = new Date(dateInput);
  const diffMs = now.getTime() - target.getTime();

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return {
    minutes,
    hours,
    days,
  };
}

export function getTimeAgoLabel(dateInput: Date | string | number) {
  const { minutes, hours, days } = getTimeDifferenceFromNow(dateInput);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute(s) ago`;
  if (hours < 24) return `${hours} hour(s) ago`;
  return `${days} day(s) ago`;
}

export const PromptsPageColumns: ColumnDef<Prompt>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllRowsSelected() ||
          (table.getIsSomeRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value: boolean) =>
          table.toggleAllRowsSelected(!!value)
        }
        className="cursor-pointer
                bg-white
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0  
                data-[state=indeterminate]:bg-[#ff5e00] data-[state=indeterminate]:border-0 data-[state=indeterminate]:ring-0 data-[state=indeterminate]:focus:ring-0 data-[state=indeterminate]:focus-visible:ring-0 data-[state=indeterminate]:text-white"
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <>
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          checked={row.getIsSelected()}
          className="cursor-pointer
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0"
          onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      </>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "text",
    header: () => <LocalizedText id="common.prompt" />,
    cell: ({ row }) => {
      const textRef = React.useRef<HTMLDivElement | null>(null);
      const [isTruncated, setIsTruncated] = React.useState(false);

      const value = row.getValue("text") as string;

      React.useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        const check = () => {
          setIsTruncated(el.scrollWidth > el.clientWidth);
        };

        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
      }, [value]);

      return (
        <div className="max-w-[550px] relative">
          <TooltipProvider delayDuration={500}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  ref={textRef}
                  className="cursor-pointer truncate overflow-hidden"
                >
                  {value}
                </div>
              </TooltipTrigger>

              {isTruncated && (
                <TooltipContent
                  side="top"
                  arrowFill="white"
                  align="start"
                  className="
                    max-w-[400px]
                    bg-white text-gray-900
                    shadow-xl border
                    whitespace-normal break-words
                  "
                >
                  {value}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  {
    accessorKey: "position",
    header: () => <LocalizedText id="common.position" />,
    cell: ({ row, table }) => {
      const isMetricLoading = Boolean(
        (table.options.meta as any)?.isPromptMetricLoading?.(row.original),
      );
      if (isMetricLoading) {
        return (
          <div className="flex justify-center">
            <Skeleton className="h-4 w-10" />
          </div>
        );
      }
      const rawValue = row.getValue("position");
      if (rawValue === null || rawValue === undefined) {
        return <div className="lowercase flex justify-center">-</div>;
      }
      const position = Number(rawValue);
      if (!Number.isFinite(position) || position <= 0) {
        return <div className="lowercase flex justify-center">-</div>;
      }
      const displayValue = Number.isFinite(position)
        ? position.toFixed(1).replace(/\.0$/, "")
        : "-";
      return (
        <div className="lowercase flex justify-center items-center leading-5">
          <span
            className="text-sm text-[#737373] font-normal leading-5"
            style={{ fontFamily: "Inter" }}
          >
            #
          </span>
          <span className="ml-1">{displayValue}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "visibility",
    header: () => <LocalizedText id="common.visibility" />,
    cell: ({ row, table }) => {
      const isMetricLoading = Boolean(
        (table.options.meta as any)?.isPromptMetricLoading?.(row.original),
      );
      if (isMetricLoading) {
        return (
          <div className="flex justify-center">
            <Skeleton className="h-4 w-12" />
          </div>
        );
      }
      const rawValue = row.getValue("visibility");
      const visibility = Number(rawValue);
      const displayValue = Number.isFinite(visibility)
        ? `${visibility.toFixed(0)}%`
        : "-";
      return (
        <div className="lowercase flex justify-center">{displayValue}</div>
      );
    },
  },
  {
    accessorKey: "country",
    header: () => <LocalizedText id="common.country" />,
    cell: ({ row }) => (
      <div className="capitalize flex gap-2 justify-start items-center">
        <div>
          {countries.find((cnt) => cnt.code === row.getValue("country"))?.icon}
        </div>
        {row.getValue("country")}
      </div>
    ),
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.created.1wtn1r9" />
          <ChevronsUpDown />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {getTimeAgoLabel(row.getValue("created_at"))}
      </div>
    ),
  },
];

export const WebQueriesColumns: ColumnDef<{
  query: string;
  provider: string;
}>[] = [
  {
    accessorKey: "#",
    header: "",
    size: 10,
    minSize: 10,
    maxSize: 10,
    cell: ({ row }) => {
      return (
        <div className="flex justify-center items-center gap-1">
          {row.index + 1}
        </div>
      );
    },
  },
  {
    accessorKey: "query",
    header: "",
    cell: ({ row }) => {
      const textRef = React.useRef<HTMLDivElement | null>(null);
      const [isTruncated, setIsTruncated] = React.useState(false);
      const value = row.original.query as string;
      const providerMeta = getProviderMeta(row.original.provider);

      React.useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        const check = () => {
          setIsTruncated(el.scrollWidth > el.clientWidth);
        };

        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
      }, [value]);

      return (
        <div className="w-full min-w-0 relative">
          <TooltipProvider delayDuration={500}>
            <Tooltip>
              <div className="flex items-center capitalize gap-1 w-full min-w-0">
                <div className="w-8">
                  {renderProviderIcon(providerMeta, 28)}
                </div>
                <TooltipTrigger asChild>
                  <div
                    ref={textRef}
                    className="flex-1 min-w-0 cursor-pointer truncate"
                  >
                    {value}
                  </div>
                </TooltipTrigger>
              </div>

              {isTruncated && (
                <TooltipContent
                  side="top"
                  arrowFill="white"
                  align="start"
                  className="
                    max-w-[400px]
                    bg-white text-gray-900
                    shadow-xl border
                    capitalize
                    whitespace-normal break-words
                  "
                >
                  {value}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
];

export const ActiveCompetitorsColumns: ColumnDef<Prompt>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value: boolean) =>
          table.toggleAllPageRowsSelected(!!value)
        }
        className="cursor-pointer
                bg-white
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0  
                data-[state=indeterminate]:bg-[#ff5e00] data-[state=indeterminate]:border-0 data-[state=indeterminate]:ring-0 data-[state=indeterminate]:focus:ring-0 data-[state=indeterminate]:focus-visible:ring-0 data-[state=indeterminate]:text-white"
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <>
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          checked={row.getIsSelected()}
          disabled={Boolean(row.original?.isOwnBrand)}
          className="cursor-pointer disabled:cursor-not-allowed
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0"
          onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      </>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "#",
    header: "#",
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1 font-semibold">
          {row.index + 1}
        </div>
      );
    },
  },
  {
    accessorKey: "domain",
    header: "Competitor",
    cell: ({ row }) => {
      const rawDomain = row.getValue("domain") as string;
      const domain = canonicalizeDomain(rawDomain) || rawDomain;
      const faviconDomain =
        (row.original as any)?.faviconDomain ||
        (row.original as any)?.domainUrl ||
        rawDomain;
      const formattedFavicon =
        typeof faviconDomain === "string" && faviconDomain.length
          ? faviconDomain.startsWith("http")
            ? faviconDomain
            : `http://${faviconDomain}`
          : "";
      const isOwnBrand = Boolean((row.original as any)?.isOwnBrand);
      return (
        <div className="cursor-pointer max-w-[550px] truncate overflow-hidden flex items-center gap-2">
          <img
            className="w-[20px] h-[20px]"
            src={
              formattedFavicon
                ? `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedFavicon}&size=64`
                : ""
            }
          />
          <span className={isOwnBrand ? "font-semibold" : ""}>{domain}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "sentiment",
    header: () => (
      <div className="flex items-center gap-2">
        <span>
          <LocalizedText id="legacy.sentiment.a9idte" />
        </span>
        <Badge variant="outline" className="text-xs px-1.5 py-0.5">
          soon
        </Badge>
      </div>
    ),
    cell: () => (
      <div className="capitalize flex gap-1">
        <div className="h-4 bg-gray-200 w-[60px] rounded-md" />
        <div className="h-4 bg-gradient-to-r from-gray-200 to-white w-[60px] rounded-md"></div>
      </div>
    ),
  },
  {
    accessorKey: "position",
    header: () => (
      <div className="flex items-center gap-2">
        <span>
          <LocalizedText id="legacy.position.1k9t5h8" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      if ((row.original as any)?.isMetricsPending) {
        return (
          <div className="flex items-center justify-end">
            <div className="h-4 w-10 rounded-md bg-gray-200" />
          </div>
        );
      }
      const rawValue = row.getValue("position");
      if (rawValue === null || rawValue === undefined) {
        return (
          <div className="flex items-center justify-end text-right">-</div>
        );
      }
      const position = Number(rawValue);
      if (!Number.isFinite(position) || position <= 0) {
        return (
          <div className="flex items-center justify-end text-right">-</div>
        );
      }
      const displayValue = Number.isFinite(position)
        ? position.toFixed(1).replace(/\.0$/, "")
        : "-";
      const isOwnBrand = Boolean((row.original as any)?.isOwnBrand);
      return (
        <div
          className={`flex items-center justify-end text-right leading-5 ${isOwnBrand ? "font-semibold" : ""}`}
        >
          <span
            className="text-sm text-[#737373] font-normal leading-5"
            style={{ fontFamily: "Inter" }}
          >
            #
          </span>
          <span className="ml-1">{displayValue}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "visibility",
    header: () => (
      <div className="flex items-center gap-2">
        <span>
          <LocalizedText id="legacy.visibility.s925mr" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      if ((row.original as any)?.isMetricsPending) {
        return (
          <div className="flex items-center justify-end">
            <div className="h-4 w-10 rounded-md bg-gray-200" />
          </div>
        );
      }
      const visibilityValue = Number(row.getValue("visibility")) || 0;
      const displayValue = `${visibilityValue.toFixed(0)}%`;
      const isOwnBrand = Boolean((row.original as any)?.isOwnBrand);
      return (
        <div
          className={`flex items-center justify-end text-right ${isOwnBrand ? "font-semibold" : ""}`}
        >
          {displayValue}
        </div>
      );
    },
  },
];

export const getSuggestedCompetitorsColumns = (
  onReject: (id: string) => void,
  onApprove: (id: string) => void,
  onCompetitorApproved?: () => void,
): ColumnDef<any>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value: boolean) =>
          table.toggleAllPageRowsSelected(!!value)
        }
        className="cursor-pointer
                bg-white
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0  
                data-[state=indeterminate]:bg-[#ff5e00] data-[state=indeterminate]:border-0 data-[state=indeterminate]:ring-0 data-[state=indeterminate]:focus:ring-0 data-[state=indeterminate]:focus-visible:ring-0 data-[state=indeterminate]:text-white"
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <>
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          checked={row.getIsSelected()}
          className="cursor-pointer
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0"
          onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      </>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "domain",
    header: "Competitor",
    cell: ({ row }) => {
      const rawDomain = row.getValue("domain") as string;
      const domain = canonicalizeDomain(rawDomain) || rawDomain;
      const faviconDomain =
        (row.original as any)?.faviconDomain ||
        (row.original as any)?.domainUrl ||
        rawDomain;
      const formattedFavicon =
        typeof faviconDomain === "string" && faviconDomain.length
          ? faviconDomain.startsWith("http")
            ? faviconDomain
            : `http://${faviconDomain}`
          : "";
      const isOwnBrand = Boolean((row.original as any)?.isOwnBrand);
      return (
        <div className="cursor-pointer flex items-center gap-2">
          <img
            className="w-[20px] h-[20px]"
            src={
              formattedFavicon
                ? `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedFavicon}&size=64`
                : ""
            }
          />
          <span className={isOwnBrand ? "font-semibold" : ""}>{domain}</span>
        </div>
      );
    },
  },
  {
    id: "mention",
    accessorFn: (row) => {
      const mention = Number((row as any)?.mention);
      if (Number.isFinite(mention)) return mention;
      const mentions = Number((row as any)?.mentions);
      return Number.isFinite(mentions) ? mentions : 0;
    },
    header: "Mentions",
    cell: ({ row }) => {
      const mentions = Number(row.getValue("mention"));
      const displayValue = Number.isFinite(mentions)
        ? Math.max(0, mentions)
        : 0;
      return (
        <div className="capitalize flex gap-2 justify-center items-center">
          {displayValue}
        </div>
      );
    },
  },
  // {
  //   accessorKey: "mentionPct",
  //   header: "Mentions",
  //   cell: ({ row }) => (
  //     <div className="capitalize flex gap-2 justify-center items-center">
  //       {row.getValue("mentionPct") ? `${parseFloat((Number(row.getValue("mentionPct"))).toFixed(1))}%` : "No"}
  //     </div>
  //   ),
  // },
  {
    accessorKey: "action",
    header: () => (
      <div className="text-end pr-16">
        <LocalizedText id="legacy.action.1530ke3" />
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="capitalize flex gap-2 justify-end">
          <Button
            className="cursor-pointer"
            size="sm"
            onClick={() => {
              onReject(row.original.id);
            }}
            variant="outline"
          >
            <X className="text-[#DC2626]" />
            <LocalizedText id="legacy.reject.1ih98sq" />
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              onApprove(row.original.id);
              onCompetitorApproved?.();
            }}
            className="cursor-pointer"
            size="sm"
          >
            <Check className="text-[#16A34A]" />{" "}
            <LocalizedText id="legacy.activate.1ps4pgc" />
          </Button>
        </div>
      );
    },
  },
];

export const getSuggestedColumns = (
  onReject: (id: string) => void,
  onApprove: (prompt: any) => void,
): ColumnDef<any>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllRowsSelected() ||
          (table.getIsSomeRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value: boolean) =>
          table.toggleAllRowsSelected(!!value)
        }
        className="cursor-pointer
                bg-white
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0  
                data-[state=indeterminate]:bg-[#ff5e00] data-[state=indeterminate]:border-0 data-[state=indeterminate]:ring-0 data-[state=indeterminate]:focus:ring-0 data-[state=indeterminate]:focus-visible:ring-0 data-[state=indeterminate]:text-white"
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        onClick={(e) => e.stopPropagation()}
        checked={row.getIsSelected()}
        className="cursor-pointer
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0"
        onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "text",
    header: () => <LocalizedText id="common.prompt" />,
    cell: ({ row }) => {
      const textRef = React.useRef<HTMLDivElement | null>(null);
      const [isTruncated, setIsTruncated] = React.useState(false);

      const value = row.getValue("text") as string;

      React.useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        const check = () => {
          setIsTruncated(el.scrollWidth > el.clientWidth);
        };

        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
      }, [value]);

      return (
        <div className="max-w-[550px] relative">
          <TooltipProvider delayDuration={500}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  ref={textRef}
                  className="cursor-pointer truncate overflow-hidden"
                >
                  {value}
                </div>
              </TooltipTrigger>

              {isTruncated && (
                <TooltipContent
                  side="top"
                  arrowFill="white"
                  align="start"
                  className="
                    max-w-[400px]
                    bg-white text-gray-900
                    shadow-xl border
                    whitespace-normal break-words
                  "
                >
                  {value}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  {
    accessorKey: "country",
    header: "Location",
    cell: ({ row }) => (
      <div className="capitalize flex gap-2 justify-start items-center">
        <div>
          {countries.find((cnt) => cnt.code === row.getValue("country"))?.icon}
        </div>
        {row.getValue("country")}
      </div>
    ),
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.created.date.1oxjamp" />
          <ChevronsUpDown />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {getTimeAgoLabel(row.getValue("created_at"))}
      </div>
    ),
  },
  {
    accessorKey: "action",
    header: () => (
      <div className="text-center">
        <LocalizedText id="legacy.action.1530ke3" />
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="capitalize flex gap-2 justify-end">
          <Button
            className="cursor-pointer"
            size="sm"
            onClick={() => {
              onReject(row.original.id);
            }}
            variant="outline"
          >
            <X className="text-[#DC2626]" />
            <LocalizedText id="legacy.reject.1ih98sq" />
          </Button>

          <Button
            onClick={() => {
              onApprove(row.original);
            }}
            className="cursor-pointer"
            size="sm"
            variant="outline"
          >
            <Check className="text-[#16A34A]" />{" "}
            <LocalizedText id="legacy.activate.1ps4pgc" />
          </Button>
        </div>
      );
    },
  },
];

const stripHtmlTags = (html: string) => {
  if (!html) return "";
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }
  return html.replace(/<[^>]*>/g, " ");
};

const decodeHtmlEntities = (value: string) => {
  if (!value) return "";
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    return doc.documentElement.textContent || "";
  }
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
};

const normalizeSnapshotText = (value?: string | null) =>
  decodeHtmlEntities(stripHtmlTags(String(value || "")))
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractAssistantHtml = (html: string) => {
  if (!html) return "";

  try {
    if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const nodes = Array.from(
        doc.querySelectorAll('div[data-message-author-role="assistant"]'),
      );
      const combined = nodes
        .map((node) => node.outerHTML.trim())
        .filter(Boolean)
        .join("<br/><br/>");
      if (combined) return combined;
    }
  } catch (e) {
    // ignore and fall back to regex
  }

  const regex =
    /<div[^>]*data-message-author-role=["']assistant["'][^>]*>[\s\S]*?<\/div>/gi;
  const matches = html.match(regex) || [];
  return matches
    .map((block) => block.trim())
    .filter(Boolean)
    .join("<br/><br/>");
};

const hasHtmlMarkup = (value: string) =>
  /<\/?[a-z][^>]*>/i.test(String(value || ""));

const escapeHtml = (value: string) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const isStandalonePunctuationLine = (line: string) =>
  /^[,.;:!?%)\]]+$/.test(String(line || "").trim());

const looksTokenizedPlainText = (lines: string[]) => {
  if (!lines.length) return false;
  const shortOrTokenLines = lines.filter((line) => {
    const normalized = String(line || "").trim();
    if (!normalized) return false;
    if (/^[•*-]$/.test(normalized)) return true;
    if (/^\d+[.)]$/.test(normalized)) return true;
    if (isStandalonePunctuationLine(normalized)) return true;
    const words = normalized.split(/\s+/).length;
    return words <= 2 && normalized.length <= 16;
  }).length;
  return lines.length >= 12 && shortOrTokenLines / lines.length >= 0.55;
};

const coalesceTokenizedLines = (lines: string[]) => {
  if (!Array.isArray(lines) || !lines.length) return "";

  const headingsRe =
    /^(?:best\s*for|key\s*features?|strengths?|pricing|summary|verdict|recommendation|pros|cons|how\s*to\s*choose|team\s*size|implementation\s*note)$/i;
  const punctuationOnlyRe = /^[,.;:!?%)\]]+$/;
  const openingPunctuationRe = /^[([{"'`]+$/;
  const markerOnlyRe = /^(?:[•*-]|\d+[.)])$/;
  const built: string[] = [];
  let current = "";

  const flush = () => {
    const normalized = current.trim();
    if (normalized) built.push(normalized);
    current = "";
  };

  const appendToken = (token: string) => {
    if (!current) {
      current = token;
      return;
    }
    if (punctuationOnlyRe.test(token)) {
      current = `${current}${token}`;
      return;
    }
    if (openingPunctuationRe.test(token)) {
      current = `${current} ${token}`;
      return;
    }
    if (/[([{/"'`-]$/.test(current)) {
      current = `${current}${token}`;
      return;
    }
    current = `${current} ${token}`;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const token = String(lines[i] || "").trim();
    if (!token) continue;

    if (markerOnlyRe.test(token)) {
      flush();
      current = token;
      continue;
    }

    const headingMatch = token.match(/^(.+?):$/);
    if (headingMatch?.[1] && headingsRe.test(String(headingMatch[1]).trim())) {
      flush();
      current = `${String(headingMatch[1]).trim()}:`;
      flush();
      continue;
    }

    appendToken(token);
    if (/[.!?]$/.test(token) && !/\d+\.$/.test(token) && current.length > 64) {
      flush();
    }
  }
  flush();

  const collapsed: string[] = [];
  for (let i = 0; i < built.length; i += 1) {
    const line = String(built[i] || "").trim();
    if (!line) continue;
    if (markerOnlyRe.test(line) && i + 1 < built.length) {
      const next = String(built[i + 1] || "").trim();
      if (next) {
        collapsed.push(`${line} ${next}`.trim());
        i += 1;
        continue;
      }
    }
    collapsed.push(line);
  }

  return collapsed
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const htmlToStructuredPlainText = (value: string) => {
  const withLineBreaks = String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|ul|ol|h[1-6]|tr|table|blockquote)>/gi, "\n");
  return decodeHtmlEntities(stripHtmlTags(withLineBreaks))
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const normalizePlainTextResponseToHtml = (raw: string, provider?: string) => {
  let text = String(raw || "")
    .replace(/\r\n/g, "\n")
    .replace(/\u00a0/g, " ")
    .trim();
  if (!text) return "";
  text = text.replace(/^\s*answer\s*:\s*short\b\s*/i, "");

  const normalizedProvider = String(provider || "").toUpperCase();
  const isFragmentedProvider =
    normalizedProvider.includes("COPILOT") ||
    normalizedProvider.includes("GROK") ||
    normalizedProvider.includes("GEMINI");
  const linesForTokenCheck = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const hasStandalonePunctuation = linesForTokenCheck.some(
    isStandalonePunctuationLine,
  );
  if (
    isFragmentedProvider &&
    (looksTokenizedPlainText(linesForTokenCheck) || hasStandalonePunctuation)
  ) {
    text =
      coalesceTokenizedLines(linesForTokenCheck) ||
      linesForTokenCheck.join(" ");
  } else {
    const lines = text
      .split("\n")
      .map((line) => line.replace(/\t/g, " ").trim());
    const coalesced: string[] = [];
    const pushLine = (value: string) => {
      if (!value && coalesced[coalesced.length - 1] === "") return;
      coalesced.push(value);
    };

    for (let i = 0; i < lines.length; i += 1) {
      const line = String(lines[i] || "").trim();
      if (!line) {
        pushLine("");
        continue;
      }
      if (/^answer\s*:\s*short\s*$/i.test(line)) {
        continue;
      }
      if (/^(?:[•*-]|\d+[.)])$/.test(line)) {
        const marker = line;
        let content = "";
        let j = i + 1;
        while (j < lines.length && !String(lines[j] || "").trim()) {
          j += 1;
        }
        if (j < lines.length) {
          content = String(lines[j] || "").trim();
          i = j;
        }
        const after = String(lines[i + 1] || "").trim();
        if (after && /^:\s*/.test(after)) {
          content = `${content}: ${after.replace(/^:\s*/, "").trim()}`.trim();
          i += 1;
        }
        pushLine(`${marker} ${content}`.trim());
        continue;
      }
      if (/^:\s+/.test(line) && coalesced.length > 0) {
        const previous = String(coalesced[coalesced.length - 1] || "").trim();
        if (previous) {
          coalesced[coalesced.length - 1] = `${previous}: ${line
            .replace(/^:\s*/, "")
            .trim()}`.trim();
          continue;
        }
      }
      pushLine(line);
    }

    text = coalesced
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  if (normalizedProvider.includes("GEMINI")) {
    text = text
      .replace(
        /^(?:o\s+)?gemini\s+(?:(?:said)(?=\b|[A-Z])|disse|dijo|a\s+dit)\s*[:.-]?\s*/i,
        "",
      )
      .trim();
  } else if (normalizedProvider.includes("COPILOT")) {
    text = text
      .replace(
        /^copilot\s+(?:(?:said)(?=\b|[A-Z])|disse|dijo|a\s+dit)\s*[:.-]?\s*/i,
        "",
      )
      .trim();
  }

  const markdownLinks: string[] = [];
  const textWithLinkPlaceholders = text.replace(
    /\[([^\]\n]{1,140})\]\((https?:\/\/[^\s)]+)\)/gi,
    (_match, label: string, href: string) => {
      let safeHref = href;
      try {
        safeHref = new URL(href).toString();
      } catch {
        safeHref = href;
      }
      const token = `__SAFE_LINK_${markdownLinks.length}__`;
      markdownLinks.push(
        `<a href="${safeHref.replace(/"/g, "%22")}" target="_blank" rel="noopener noreferrer">${escapeHtml(
          label,
        )}</a>`,
      );
      return token;
    },
  );

  let escaped = escapeHtml(textWithLinkPlaceholders);
  markdownLinks.forEach((html, index) => {
    escaped = escaped.replace(`__SAFE_LINK_${index}__`, html);
  });

  escaped = escaped.replace(
    /(^|\n)\s*\[?(\d+)\]?\s+(https?:\/\/[^\s<]+)/g,
    (_match, leading: string, idx: string, href: string) =>
      `${leading}<a href="${href.replace(
        /"/g,
        "%22",
      )}" target="_blank" rel="noopener noreferrer">[${idx}]</a>`,
  );

  const paragraphs = escaped
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${part.replace(/\n/g, "<br/>")}</p>`);

  return paragraphs.join("");
};

const normalizeResponseForRender = (raw: string, provider?: string) => {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (hasHtmlMarkup(text)) {
    const providerKey = String(provider || "").toUpperCase();
    const isFragmentedProvider =
      providerKey.includes("COPILOT") ||
      providerKey.includes("GROK") ||
      providerKey.includes("GEMINI");
    if (!isFragmentedProvider) return text;
    const structuredPlainText = htmlToStructuredPlainText(text);
    const lines = structuredPlainText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    const hasBrokenBulletShape =
      /(^|\n)(?:[•*-]|\d+[.)])\s*\n/i.test(structuredPlainText) ||
      /\n:\s*[^\n]+/i.test(structuredPlainText);
    const hasStandalonePunctuation = lines.some(isStandalonePunctuationLine);
    const hasFragmentedProviderAttribution =
      /^(?:o\s+)?(?:gemini|copilot)\s*$/i.test(lines[0] || "");
    if (
      looksTokenizedPlainText(lines) ||
      hasBrokenBulletShape ||
      hasStandalonePunctuation ||
      hasFragmentedProviderAttribution
    ) {
      return normalizePlainTextResponseToHtml(structuredPlainText, provider);
    }
    return text;
  }
  return normalizePlainTextResponseToHtml(text, provider);
};

const sanitizeResponseHtml = (html: string) => {
  if (!html) return "";
  const fallbackSanitized = html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\sstyle=(".*?"|'.*?')/gi, "");

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return fallbackSanitized;
  }

  const isCitationAnchor = (anchor: HTMLAnchorElement) => {
    const normalizeToken = (value: string) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const matchesHostLabel = (href: string, label: string) => {
      const host = toCitationHost(href);
      if (!host) return false;
      const hostToken = normalizeToken(host);
      const hostNoTld = host.split(".").slice(0, -1).join(".");
      const hostNoTldToken = normalizeToken(hostNoTld);
      const labelToken = normalizeToken(label);
      if (!labelToken) return false;
      return (
        labelToken === hostToken ||
        labelToken === hostNoTldToken ||
        hostToken.startsWith(labelToken) ||
        (hostNoTldToken && labelToken.startsWith(hostNoTldToken))
      );
    };
    const hasCitationCountMarker = () => {
      const countRe = /^\s*(?:\(?\+?\d+\+?\)?|\[\d+\])\s*$/;
      const next = anchor.nextSibling;
      if (next && next.nodeType === Node.TEXT_NODE) {
        const text = String(next.nodeValue || "");
        if (countRe.test(text)) return true;
      }
      if (next && next.nodeType === Node.ELEMENT_NODE) {
        const text = String((next as HTMLElement).textContent || "").trim();
        if (countRe.test(text)) return true;
      }
      return false;
    };
    const hasInlineCitationCountMarker = (label: string) =>
      /\s*(?:\d+\+|\+\d+|\[\d+\])\s*$/.test(String(label || "").trim());
    const hasNumericCitationLabel = (label: string) =>
      /^\s*(?:\[\d+\]|\d+)\s*$/.test(String(label || "").trim());
    const isDomainLikeLabel = (value: string) => {
      const normalized = String(value || "")
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\.\.\.+$/, "")
        .trim()
        .toLowerCase();
      if (!normalized) return false;
      if (/\s/.test(normalized)) return false;
      return /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(normalized);
    };
    const ownClass = String(anchor.getAttribute("class") || "").toLowerCase();
    const parentClass = String(
      anchor.parentElement?.getAttribute("class") || "",
    ).toLowerCase();
    const hasCitationClass =
      ownClass.includes("citation") ||
      ownClass.includes("inline-citation-link") ||
      ownClass.includes("extracted-citation-link") ||
      parentClass.includes("citation");
    if (hasCitationClass) return true;
    if (
      anchor.hasAttribute("data-citation-index") ||
      anchor.hasAttribute("data-citation-uuid")
    ) {
      return true;
    }
    if (anchor.closest('[data-testid="webpage-citation-pill"]')) {
      return true;
    }
    const href = String(anchor.getAttribute("href") || "").trim();
    const label = String(anchor.textContent || "").trim();
    const parentTag = String(anchor.parentElement?.tagName || "").toLowerCase();
    if (href && (parentTag === "sup" || hasNumericCitationLabel(label))) {
      return true;
    }

    // Some Perplexity variants lose citation classes but keep "domain + 1+" patterns.
    const labelSansCount = label
      .replace(/\s*(?:\d+\+|\+\d+|\[\d+\])\s*$/g, "")
      .trim();
    if (
      href &&
      (hasCitationCountMarker() || hasInlineCitationCountMarker(label)) &&
      matchesHostLabel(href, labelSansCount || label)
    ) {
      return true;
    }
    if (
      href &&
      matchesHostLabel(href, labelSansCount || label) &&
      (isDomainLikeLabel(labelSansCount || label) ||
        Boolean(anchor.querySelector("img, svg")))
    ) {
      return true;
    }

    return false;
  };

  const toCitationHost = (href: string) => {
    try {
      const parsed = new URL(href, window.location.origin);
      return parsed.hostname.replace(/^www\./i, "");
    } catch {
      return "";
    }
  };

  const toCitationLabel = (href: string, fallbackText: string) => {
    const host = toCitationHost(href);
    if (host) return host;
    const fallback = String(fallbackText || "")
      .replace(/\s+/g, " ")
      .trim();
    return fallback || "source";
  };

  const toFaviconUrl = (host: string) =>
    `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`;
  const isDomainToken = (value: string) =>
    /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(
      String(value || "")
        .replace(/^www\./i, "")
        .trim(),
    );
  const isCitationDumpText = (value: string) => {
    const normalized = String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return false;
    const tokens = normalized
      .split(" ")
      .map((token) =>
        token
          .replace(/^[\[(,;]+/, "")
          .replace(/[\]),;:.!?]+$/, "")
          .trim(),
      )
      .filter(Boolean);
    if (tokens.length < 3) return false;
    const withoutCounters = tokens.filter((token) => !/^\+\d+$/.test(token));
    if (withoutCounters.length < 3) return false;
    const helperCount = withoutCounters.filter((token) =>
      /^(?:reddit|sources?|citations?)$/i.test(token),
    ).length;
    const domainCount = withoutCounters.filter((token) =>
      isDomainToken(token),
    ).length;
    return (
      domainCount >= 3 && domainCount + helperCount >= withoutCounters.length
    );
  };
  const stripTrailingCitationDump = (value: string) => {
    let normalized = String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return "";

    const patterns = [
      /\s+(?:reddit|sources?|citations?)\s*\+\d+\s+(?:(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\s+|$)){2,}$/i,
      /\s+(?:(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\s+|$)){3,}$/i,
    ];
    for (const pattern of patterns) {
      if (pattern.test(normalized)) {
        normalized = normalized.replace(pattern, "").trim();
      }
    }
    return normalized;
  };
  const extractCitationDomains = (value: string) => {
    const normalized = String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return [] as string[];

    const tokens = normalized
      .split(" ")
      .map((token) =>
        token
          .replace(/^[\[(,;]+/, "")
          .replace(/[\]),;:.!?]+$/, "")
          .trim(),
      )
      .filter(Boolean);

    const result: string[] = [];
    const seen = new Set<string>();
    tokens.forEach((token) => {
      if (/^(?:reddit|sources?|citations?)$/i.test(token)) return;
      if (/^(?:\+\d+|\d+\+|\[\d+\])$/i.test(token)) return;

      const normalizedDomain = token
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .toLowerCase();

      if (!isDomainToken(normalizedDomain)) return;
      if (seen.has(normalizedDomain)) return;

      seen.add(normalizedDomain);
      result.push(normalizedDomain);
    });

    return result;
  };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(fallbackSanitized, "text/html");

    // Drop unsafe or irrelevant dynamic nodes from provider HTML.
    doc
      .querySelectorAll(
        "script, style, noscript, iframe, object, embed, template, source-footnote, sources-carousel-inline, source-inline-chip, mat-icon",
      )
      .forEach((node) => node.remove());

    // Remove interactive controls that leak from provider UIs.
    doc
      .querySelectorAll("button, form, input, select, option, textarea")
      .forEach((node) => node.remove());

    const allowedAttrsByTag: Record<string, Set<string>> = {
      a: new Set([
        "href",
        "target",
        "rel",
        "class",
        "data-citation-index",
        "data-citation-uuid",
        "data-testid",
      ]),
      img: new Set([
        "src",
        "alt",
        "width",
        "height",
        "class",
        "loading",
        "decoding",
        "referrerpolicy",
      ]),
      default: new Set(["class", "data-testid", "colspan", "rowspan"]),
    };

    doc.querySelectorAll("*").forEach((el) => {
      el.removeAttribute("style");
      Array.from(el.attributes).forEach((attr) => {
        if (/^on/i.test(attr.name)) {
          el.removeAttribute(attr.name);
          return;
        }
        const tag = el.tagName.toLowerCase();
        const allowed = allowedAttrsByTag[tag] || allowedAttrsByTag.default;
        if (!allowed.has(attr.name.toLowerCase())) {
          el.removeAttribute(attr.name);
        }
      });
    });

    const anchors = Array.from(
      doc.querySelectorAll("a[href]"),
    ) as HTMLAnchorElement[];
    anchors.forEach((anchor) => {
      const href = String(anchor.getAttribute("href") || "").trim();
      if (!href) return;

      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");

      const citationAnchor = isCitationAnchor(anchor);
      if (!citationAnchor) return;

      const next = anchor.nextSibling;
      const countRe = /^\s*(?:\(?\+?\d+\+?\)?|\[\d+\])\s*$/;
      if (next && next.nodeType === Node.TEXT_NODE) {
        const text = String(next.nodeValue || "");
        if (countRe.test(text)) {
          next.nodeValue = "";
        }
      } else if (next && next.nodeType === Node.ELEMENT_NODE) {
        const el = next as HTMLElement;
        if (countRe.test(String(el.textContent || "").trim())) {
          el.remove();
        }
      }
      const anchorCountRe = /\s*(?:\d+\+|\+\d+|\[\d+\])\s*$/;
      const currentAnchorText = String(anchor.textContent || "");
      if (anchorCountRe.test(currentAnchorText)) {
        anchor.textContent = currentAnchorText
          .replace(anchorCountRe, "")
          .trim();
      }

      const host = toCitationHost(href);
      const label = toCitationLabel(href, anchor.textContent || "");

      anchor.classList.remove("inline-citation-link");
      anchor.classList.add("citation-chip-link");

      anchor.textContent = "";

      if (host) {
        const iconShell = doc.createElement("span");
        iconShell.className = "citation-chip-icon-shell";
        const icon = doc.createElement("img");
        icon.className = "citation-chip-icon";
        icon.alt = "";
        icon.width = 14;
        icon.height = 14;
        icon.loading = "lazy";
        icon.decoding = "async";
        icon.referrerPolicy = "no-referrer";
        icon.src = toFaviconUrl(host);
        iconShell.appendChild(icon);
        anchor.appendChild(iconShell);
      }

      const text = doc.createElement("span");
      text.className = "citation-chip-label";
      text.textContent = label;
      anchor.appendChild(text);
    });

    // Remove plain trailing citation dumps like "domain.com domain2.com ..."
    // so fallback chip rendering can present clean linked chips instead.
    Array.from(doc.body.querySelectorAll("p, div, span, li")).forEach(
      (node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.querySelector("a")) return;
        const textValue = String(node.textContent || "").trim();
        if (!textValue) return;

        const replaceWithCitationChips = (domains: string[]) => {
          if (!domains.length) return false;
          const row = doc.createElement("div");
          row.className = "inline-citation-row";
          domains.forEach((domain) => {
            const href = `https://${domain}`;
            const chip = doc.createElement("a");
            chip.className = "citation-chip-link";
            chip.href = href;
            chip.target = "_blank";
            chip.rel = "noopener noreferrer";

            const iconShell = doc.createElement("span");
            iconShell.className = "citation-chip-icon-shell";
            const icon = doc.createElement("img");
            icon.className = "citation-chip-icon";
            icon.alt = "";
            icon.width = 14;
            icon.height = 14;
            icon.loading = "lazy";
            icon.decoding = "async";
            icon.referrerPolicy = "no-referrer";
            icon.src = toFaviconUrl(domain);
            iconShell.appendChild(icon);

            const label = doc.createElement("span");
            label.className = "citation-chip-label";
            label.textContent = domain;

            chip.appendChild(iconShell);
            chip.appendChild(label);
            row.appendChild(chip);
          });

          node.replaceWith(row);
          return true;
        };

        if (isCitationDumpText(textValue)) {
          const domains = extractCitationDomains(textValue);
          if (replaceWithCitationChips(domains)) {
            return;
          }
        }

        if (node.children.length > 0) return;

        const textWithoutCitationTail = stripTrailingCitationDump(textValue);
        if (textWithoutCitationTail !== textValue) {
          if (!textWithoutCitationTail) {
            node.remove();
            return;
          }
          node.textContent = textWithoutCitationTail;
          return;
        }
        if (/^(?:reddit|sources?|citations?)\s*\+\d+\s*$/i.test(textValue)) {
          node.remove();
          return;
        }
        if (isCitationDumpText(textValue)) {
          const domains = extractCitationDomains(textValue);
          if (!replaceWithCitationChips(domains)) {
            node.remove();
          }
        }
      },
    );

    return doc.body.innerHTML || "";
  } catch {
    return fallbackSanitized;
  }
};

const ProviderResponseHtml = ({
  html,
  provider,
}: {
  html: string;
  provider: string;
}) => {
  const htmlToRender = sanitizeResponseHtml(html);

  return (
    <div
      className="response-html"
      data-provider={provider.toLowerCase()}
      dangerouslySetInnerHTML={{
        __html: htmlToRender,
      }}
    ></div>
  );
};

export const buildResponsePreview = (html: string, maxChars = 200) => {
  const text = decodeHtmlEntities(stripHtmlTags(html))
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars - 3).trimEnd()}...`;
};

const formatRunDate = (value: unknown) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" && value.toLowerCase() === "in progress") {
    return value;
  }

  const date = new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day} ${month}, ${year}`;
};

const runHasResponseContent = (run: Partial<Domains> & Record<string, any>) => {
  const raw = String(
    run?.responseHtml || run?.responseText || run?.responsePreview || "",
  ).trim();
  if (!raw) return false;
  const normalized = stripHtmlTags(raw).replace(/\s+/g, " ").trim();
  const normalizedToken = normalized.toLowerCase();
  const normalizedTerminal = normalizedToken.replace(/[.!?]+$/g, "").trim();
  if (
    /^(?:no\s+response(?:\s+captured(?:\s+yet)?)?|not\s*triggered|failed|in\s*progress)$/i.test(
      normalizedTerminal,
    )
  ) {
    return false;
  }
  return Boolean(normalized);
};

const runFallbackCitationUrls = (
  run: Partial<Domains> & Record<string, any>,
) => {
  const pages = Array.isArray(run?.citedPages) ? run.citedPages : [];
  return Array.from(
    new Set(
      pages
        .filter((page: any) => {
          const sourceType = String(page?.sourceType || "")
            .trim()
            .toLowerCase();
          if (sourceType) return sourceType === "citation";
          return page?.isMentioned !== true;
        })
        .map((page: any) => String(page?.url || "").trim())
        .filter(Boolean),
    ),
  );
};

const citationLabelFromUrl = (url: string) => {
  const domain = canonicalizeDomain(url);
  if (domain) return domain;
  return String(url || "")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split(/[/?#]/)[0]
    .trim();
};

const citationFaviconFromUrl = (url: string) => {
  const label = citationLabelFromUrl(url);
  if (!label) return "";
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(label)}&sz=32`;
};

type ProviderMeta = {
  label: string;
  icon: string;
  iconSrc?: string;
  iconScale?: number;
};

const getProviderMeta = (provider?: string): ProviderMeta => {
  const key = String(provider || "").toUpperCase();
  if (!key) {
    return {
      label: "Unknown",
      icon: "lucide:bot",
    };
  }
  if (key === "MULTI" || key === "ALL") {
    return {
      label: "Multiple platforms",
      icon: "lucide:bot",
    };
  }
  if (key.includes("PERP")) {
    return {
      label: "Perplexity",
      icon: "logos:perplexity-icon",
    };
  }
  if (
    key.includes("AI_MODE") ||
    key.includes("AI-MODE") ||
    key.includes("AIMODE")
  ) {
    return {
      label: "AI Mode",
      icon: "flat-color-icons:google",
      iconSrc: aiModeIcon,
    };
  }
  if (key.includes("GEMINI")) {
    return {
      label: "Gemini",
      icon: "material-icon-theme:gemini-ai",
    };
  }
  if (
    key.includes("GOOGLE") ||
    key.includes("AIO") ||
    key.includes("OVERVIEW")
  ) {
    return {
      label: "Google AI Overviews",
      icon: "flat-color-icons:google",
    };
  }
  if (key.includes("GROK")) {
    return {
      label: "Grok",
      icon: "lucide:bot",
      iconSrc: grokIcon,
    };
  }
  if (key.includes("COPILOT") || key.includes("MICROSOFT")) {
    return {
      label: "Copilot",
      icon: "lucide:bot",
      iconSrc: copilotIcon,
    };
  }
  if (
    key.includes("GPT") ||
    key.includes("CHATGPT") ||
    key.includes("OPENAI")
  ) {
    return {
      label: "ChatGPT",
      icon: "hugeicons:chat-gpt",
    };
  }
  return {
    label: key,
    icon: "lucide:bot",
  };
};

const renderProviderIcon = (providerMeta: ProviderMeta, size: number) => {
  if (providerMeta.iconSrc) {
    return (
      <img
        src={providerMeta.iconSrc}
        alt={providerMeta.label}
        width={size}
        height={size}
        style={{ maxWidth: `${size}px` }}
        className="object-contain shrink-0"
      />
    );
  }
  return <Icon icon={providerMeta.icon} width={size} height={size} />;
};

const normalizeProviders = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value.map((provider) => String(provider || "").trim()).filter(Boolean),
      ),
    );
  }
  if (typeof value === "string") {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((provider) => provider.trim())
          .filter(Boolean),
      ),
    );
  }
  return [];
};

const normalizeProviderRunKey = (value?: string | null): string => {
  const key = String(value || "").toUpperCase();
  if (!key) return "";
  if (key.includes("PERP")) return "PERP";
  if (
    key.includes("AI_MODE") ||
    key.includes("AI-MODE") ||
    key.includes("AIMODE")
  ) {
    return "AI_MODE";
  }
  if (key.includes("GEMINI")) return "GEMINI";
  if (
    key.includes("GOOGLE") ||
    key.includes("AIO") ||
    key.includes("OVERVIEW")
  ) {
    return "GOOGLE_OVERVIEW";
  }
  if (key.includes("GROK")) return "GROK";
  if (key.includes("COPILOT") || key.includes("MICROSOFT")) return "COPILOT";
  if (key.includes("GPT")) return "GPT";
  return key;
};

const toRunDateKey = (value?: string | null): string => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const direct = raw.match(/^\d{4}-\d{2}-\d{2}/);
  if (direct?.[0]) return direct[0];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

export type ExecutionDetailsRunRef = {
  runId?: string;
  fallbackRunKey?: string;
  baseFallbackRunKey?: string;
};

export const buildExecutionRunRef = (
  run: Partial<Domains> & Record<string, any>,
): ExecutionDetailsRunRef => {
  const runId = String(run?.id || "").trim();
  const providerValue = normalizeProviderRunKey(run?.provider);
  const runDateValue = toRunDateKey(
    run?.runDate || run?.createdAt || run?.created_at || "",
  );
  const createdAtValue = String(run?.createdAt || run?.created_at || "").trim();
  const baseFallbackRunKey = [providerValue, runDateValue]
    .filter(Boolean)
    .join("|");
  const detailedFallbackRunKey = [providerValue, runDateValue, createdAtValue]
    .filter(Boolean)
    .join("|");
  const fallbackRunKey = detailedFallbackRunKey || baseFallbackRunKey;

  return {
    runId: runId || undefined,
    fallbackRunKey: fallbackRunKey || undefined,
    baseFallbackRunKey: baseFallbackRunKey || undefined,
  };
};

export const matchExecutionRunRef = (
  run: Partial<Domains> & Record<string, any>,
  runRef?: ExecutionDetailsRunRef | null,
) => {
  if (!runRef) return false;
  const currentRef = buildExecutionRunRef(run);
  const hasRunIdMatch = Boolean(
    runRef.runId && currentRef.runId && runRef.runId === currentRef.runId,
  );
  if (hasRunIdMatch) return true;
  const hasFallbackMatch = Boolean(
    runRef.fallbackRunKey &&
      currentRef.fallbackRunKey &&
      runRef.fallbackRunKey === currentRef.fallbackRunKey,
  );
  if (hasFallbackMatch) return true;
  const hasBaseFallbackMatch = Boolean(
    !runRef.runId &&
      !currentRef.runId &&
      runRef.baseFallbackRunKey &&
      currentRef.baseFallbackRunKey &&
      runRef.baseFallbackRunKey === currentRef.baseFallbackRunKey,
  );
  return hasBaseFallbackMatch;
};

export const ExecutionDetailsSheet = ({
  open,
  onOpenChange,
  run,
  responseLoading = false,
  country,
  text,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: (Partial<Domains> & Record<string, any>) | null;
  responseLoading?: boolean;
  country: string;
  text: string;
}) => {
  const { t } = useLocale();
  const hasRun = Boolean(run);
  const runProvider = String(run?.provider || "");
  const responseRawHtml =
    String((run as any)?.responseHtml || "").trim() ||
    String(run?.responseText || "").trim() ||
    String((run as any)?.responsePreview || "").trim() ||
    "";
  const rawHtml = responseLoading ? "" : responseRawHtml;
  const isLoadingRow = Boolean(run?.isLoadingRow);
  const rowStatus = String(run?.status || "").toLowerCase();
  const isNotTriggeredRow =
    rowStatus === "not_triggered" ||
    rowStatus === "never_run" ||
    rawHtml.toLowerCase() === "not triggered";
  const isFailedRow = rowStatus === "failed" || rowStatus === "quota_exceeded";
  const hasResponseContent = runHasResponseContent({
    ...(run || {}),
    responseHtml: responseRawHtml,
    responseText: responseRawHtml,
  } as any);
  const isNoResponseRow =
    hasRun &&
    !isLoadingRow &&
    !isNotTriggeredRow &&
    !isFailedRow &&
    !hasResponseContent;
  const providerMeta = getProviderMeta(runProvider);
  const providerKey = runProvider.toUpperCase();
  const normalizedRawHtml = normalizeResponseForRender(rawHtml, runProvider);
  const isChatGptLikeProvider =
    providerKey.includes("GPT") ||
    providerKey.includes("CHATGPT") ||
    providerKey.includes("OPENAI");
  const assistantHtml = isChatGptLikeProvider
    ? extractAssistantHtml(normalizedRawHtml)
    : "";
  const htmlToRender = assistantHtml || normalizedRawHtml;
  const sanitizedHtml = sanitizeResponseHtml(htmlToRender);
  const fallbackCitationUrls = runFallbackCitationUrls((run || {}) as any);
  const hasInlineCitationChips =
    /citation-chip-link|inline-citation-link|extracted-citation-link|webpage-citation-pill/i.test(
      sanitizedHtml,
    );
  const shouldRenderFallbackCitations =
    !isLoadingRow &&
    !isNotTriggeredRow &&
    !isFailedRow &&
    !isNoResponseRow &&
    fallbackCitationUrls.length > 0 &&
    !hasInlineCitationChips;
  const shouldShowResponseLoading = responseLoading && !isLoadingRow;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl p-4">
        <div className="flex justify-between bg-white z-10">
          <div className="flex gap-2 items-center">
            <SheetClose asChild>
              <Button variant="outline" className="cursor-pointer">
                <ArrowLeft />
                <LocalizedText id="legacy.back.yjmdam" />
              </Button>
            </SheetClose>
            <Separator orientation="vertical" />
            <div className="font-semibold text-base text-[#0A0A0A]">
              <LocalizedText id="legacy.execution.details.ieh02d" />
            </div>
          </div>
        </div>
        <SheetHeader className="flex flex-row justify-between border rounded-lg">
          <SheetTitle className="sr-only">
            <LocalizedText id="legacy.execution.details.ieh02d" />
          </SheetTitle>
          <div className="flex w-full justify-between">
            <div className="flex items-center ml-4 flex gap-2 mr-3 text-muted-foreground text-sm font-normal">
              <div>
                <LocalizedText id="legacy.status.1iwhbo1" />
              </div>
              <Badge variant="outline" className="flex items-center gap-2">
                {!hasRun ? (
                  <>
                    <CircleDashed size={16} className="text-muted-foreground" />
                    <LocalizedText id="legacy.unavailable.11324pz" />
                  </>
                ) : isLoadingRow ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-[#FF5E00]" />
                    <LocalizedText id="legacy.running.brblhe" />
                  </>
                ) : isNotTriggeredRow || isNoResponseRow ? (
                  <>
                    <CircleDashed size={16} className="text-muted-foreground" />
                    {isNoResponseRow ? (
                      <LocalizedText id="common.noResponse" />
                    ) : (
                      <LocalizedText id="common.notTriggered" />
                    )}
                  </>
                ) : isFailedRow ? (
                  <>
                    <AlertCircle size={16} className="text-[#CE2C31]" />
                    <LocalizedText id="legacy.failed.15m090m" />
                  </>
                ) : (
                  <>
                    <BadgeCheck size={16} className="text-[#16A34A]" />
                    <LocalizedText id="legacy.succeeded.k53eg6" />
                  </>
                )}
              </Badge>
            </div>
            <Separator orientation="vertical" />
            <div className="flex items-center gap-2 ml-4 mr-3 text-muted-foreground text-sm font-normal">
              <div>
                <LocalizedText id="legacy.platforms.harnzd" />
              </div>
              <div className="flex items-center text-[#0A0A0A]">
                {renderProviderIcon(providerMeta, 28)} {providerMeta.label}
              </div>
            </div>
            <Separator orientation="vertical" />
            <div className="flex items-center gap-2 ml-4 mr-3 text-muted-foreground text-sm font-normal">
              <div>
                <LocalizedText id="legacy.country.1oy8cul" />
              </div>
              {countries.find((cnt) => cnt.code === country)?.icon}
              {country}
            </div>
          </div>
        </SheetHeader>
        <div className="flex flex-col p-6 gap-2 border rounded-md mb-5 h-[calc(100vh-160px)] overflow-y-scroll overflow-x-hidden">
          <div className="flex justify-end gap-2">
            <div className="text-sm p-2 rounded-md bg-[#F5F5F5]">{text}</div>
            <Avatar>
              <AvatarImage
                src="https://www.gravatar.com/avatar/?d=mp&s=80"
                alt={t("legacy.default.avatar.15jwjgf")}
              />
              <AvatarFallback>CN</AvatarFallback>
            </Avatar>
          </div>
          <div className="flex items-start gap-2">
            <div className="flex shrink-0 items-start">
              {renderProviderIcon(providerMeta, 28)}
            </div>
            {!hasRun ? (
              <div className="border p-3 text-sm rounded-md bg-[#F5F5F5] h-max text-muted-foreground">
                <LocalizedText id="legacy.execution.details.are.not.available.for.this.run.14c5vr1" />
              </div>
            ) : isLoadingRow || shouldShowResponseLoading ? (
              <div className="border p-4 text-sm rounded-md bg-[#F5F5F5] h-max flex items-center gap-3 text-muted-foreground">
                <div className="flex flex-row gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#84888b] animate-bounce [animation-delay:.7s]"></div>
                  <div className="w-2 h-2 rounded-full bg-[#84888b] animate-bounce [animation-delay:.3s]"></div>
                  <div className="w-2 h-2 rounded-full bg-[#84888b] animate-bounce [animation-delay:.7s]"></div>
                </div>
              </div>
            ) : isNotTriggeredRow || isNoResponseRow ? (
              <div className="border p-3 text-sm rounded-md bg-[#F5F5F5] h-max text-muted-foreground">
                <LocalizedText id="legacy.this.provider.did.not.return.a.response.for.1htihv0" />
              </div>
            ) : hasResponseContent && sanitizedHtml ? (
              <div className="border p-3 rounded-md bg-[#F5F5F5] h-max">
                <ProviderResponseHtml
                  html={sanitizedHtml}
                  provider={runProvider}
                />
                {shouldRenderFallbackCitations ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {fallbackCitationUrls.map((url) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="citation-chip-link"
                      >
                        <span className="citation-chip-icon-shell">
                          <img
                            className="citation-chip-icon"
                            alt=""
                            width={14}
                            height={14}
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                            src={citationFaviconFromUrl(url)}
                          />
                        </span>
                        <span className="citation-chip-label">
                          {citationLabelFromUrl(url) || "source"}
                        </span>
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="border p-3 text-sm rounded-md bg-[#F5F5F5] h-max text-muted-foreground">
                <LocalizedText id="legacy.no.response.captured.yet.1h3m9lt" />
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const renderProviderIcons = (providers: string[], size?: string) => {
  const normalized = normalizeProviders(providers);
  if (!normalized.length) {
    return <div className="text-muted-foreground">-</div>;
  }
  return (
    <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
      {normalized.slice(0, 6).map((provider) => {
        const providerMeta = getProviderMeta(provider);
        return (
          <Avatar key={provider} className={`border bg-white ${size && size}`}>
            <AvatarFallback title={providerMeta.label}>
              {renderProviderIcon(providerMeta, 16)}
            </AvatarFallback>
          </Avatar>
        );
      })}
    </div>
  );
};

const getInitials = (value?: string) => {
  if (!value) return "";
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] ?? "";
  const second =
    parts.length > 1 ? (parts[1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
  return `${first}${second}`.toUpperCase();
};

export const promptsDetailsColumns = (
  mainBrandKey?: string,
  options?: {
    brandKeyLabels?: Map<string, string>;
    selectedBrandKeys?: Set<string>;
    resolveBrandKey?: (value?: string | null, isMain?: boolean) => string;
    onExecutionDetailsOpenChange?: (
      runRef: ExecutionDetailsRunRef,
      open: boolean,
    ) => void;
  },
): ColumnDef<Domains>[] => [
  {
    accessorKey: "runDate",
    size: 140,
    header: ({ column }) => {
      return (
        <div className="w-[140px]">
          <Button
            variant="ghost"
            className="w-full justify-between"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            <LocalizedText id="legacy.execution.date.1vk6gw3" />
            <ChevronsUpDown />
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="capitalize flex justify-start px-5 w-[140px]">
          {formatRunDate(row.getValue("runDate"))}
        </div>
      );
    },
  },
  {
    accessorKey: "responseText",
    header: () => <LocalizedText id="common.response" />,
    cell: ({ row }) => {
      const runRef = buildExecutionRunRef(row?.original || {});
      const rawHtml: string =
        String(row.getValue("responseText") || "").trim() ||
        String((row?.original as any)?.responseHtml || "").trim() ||
        String((row?.original as any)?.responsePreview || "").trim() ||
        "";
      const isLoadingRow = Boolean(row?.original?.isLoadingRow);
      const rowStatus = String(row?.original?.status || "").toLowerCase();
      const isNotTriggeredRow =
        rowStatus === "not_triggered" ||
        rowStatus === "never_run" ||
        rawHtml.toLowerCase() === "not triggered";
      const isFailedRow =
        rowStatus === "failed" || rowStatus === "quota_exceeded";
      const hasResponseContent = runHasResponseContent({
        ...(row?.original || {}),
        responseHtml: rawHtml,
        responseText: rawHtml,
      } as any);
      const providerMeta = getProviderMeta(row?.original?.provider);
      const providerKey = String(row?.original?.provider || "").toUpperCase();
      const normalizedRawHtml = normalizeResponseForRender(
        rawHtml,
        row?.original?.provider,
      );
      const isChatGptLikeProvider =
        providerKey.includes("GPT") ||
        providerKey.includes("CHATGPT") ||
        providerKey.includes("OPENAI");
      const assistantHtml = isChatGptLikeProvider
        ? extractAssistantHtml(normalizedRawHtml)
        : "";
      const htmlToRender = assistantHtml || normalizedRawHtml;
      const previewText = isLoadingRow
        ? "In Progress"
        : isNotTriggeredRow
          ? "Not Triggered"
          : isFailedRow
            ? "Failed"
            : !hasResponseContent
              ? "No response"
              : buildResponsePreview(htmlToRender);
      const handleOpen = () => {
        options?.onExecutionDetailsOpenChange?.(runRef, true);
      };
      return (
        <div className="truncate whitespace-nowrap">
          <div
            className="flex gap-2 items-center cursor-pointer max-w-[500px] truncate"
            onClick={handleOpen}
          >
            <div>{renderProviderIcon(providerMeta, 28)}</div>
            {isLoadingRow ? (
              <div className="inline-flex items-center gap-1 rounded-lg border border-[#E5E5E5] bg-white px-2 py-1 text-sm text-[#0A0A0A]">
                <span
                  className="burst-spinner text-[#2563EB]"
                  aria-hidden="true"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
                <span>
                  <LocalizedText id="legacy.in.progress.1dy9jmn" />
                </span>
              </div>
            ) : isNotTriggeredRow ? (
              <Badge variant="outline" className="text-muted-foreground">
                <LocalizedText id="legacy.not.triggered.1r4vuix" />
              </Badge>
            ) : !hasResponseContent ? (
              <Badge variant="outline" className="text-muted-foreground">
                <LocalizedText id="legacy.no.response.hwty1z" />
              </Badge>
            ) : (
              <div
                className={`max-w-[560px] truncate ${previewText ? "hover:underline hover:underline-offset-2" : ""}`}
                title={previewText}
              >
                {previewText || "No response"}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "position",
    header: () => <LocalizedText id="common.position" />,
    cell: ({ row }: { row: any }) => {
      const isLoadingRow = Boolean(row?.original?.isLoadingRow);
      if (isLoadingRow) {
        return <div className="flex items-center justify-center">-</div>;
      }
      const renderPosition = (value: number) => {
        const displayValue = value.toFixed(1).replace(/\.0$/, "");
        return (
          <div className="flex items-center justify-center leading-5">
            <span
              className="text-sm text-[#737373] font-normal leading-5"
              style={{ fontFamily: "Inter" }}
            >
              #
            </span>
            <span className="ml-1">{displayValue}</span>
          </div>
        );
      };
      const normalizeBrandKey = (value: string) =>
        String(value || "")
          .trim()
          .toLowerCase();
      const normalizeSearchValue = (value?: string | null) =>
        String(value || "")
          .toLowerCase()
          .replace(/<[^>]*>/g, " ")
          .replace(/\([^)]*\)/g, " ")
          .replace(/[^a-z0-9.]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
      const canonicalMain = normalizeBrandKey(mainBrandKey || "");
      const resolveBrandKey =
        options?.resolveBrandKey ||
        ((value?: string | null, isMain?: boolean) =>
          isMain && canonicalMain
            ? canonicalMain
            : normalizeBrandKey(value || ""));
      const selectedBrandKeys = options?.selectedBrandKeys;
      const citedBrands = Array.isArray(row?.original?.citedBrands)
        ? row.original.citedBrands
        : [];
      if (!canonicalMain) {
        return <div className="flex items-center justify-center">-</div>;
      }
      const hasMention = citedBrands.some((brand: any) => {
        const key = resolveBrandKey(
          brand?.brandKey || brand?.brandDisplay || "",
          Boolean(brand?.isMain),
        );
        if (!key || key !== canonicalMain) return false;
        const hasMentionedPage = Array.isArray(brand?.citedPages)
          ? brand.citedPages.some(
              (p: any) =>
                p?.isMentioned &&
                (!p?.sourceType || p.sourceType === "mention"),
            )
          : false;
        return hasMentionedPage;
      });
      if (!hasMention) {
        return <div className="flex items-center justify-center">-</div>;
      }

      const rawResponseText = String(row?.original?.responseText || "");
      const normalizedResponseText = normalizeSearchValue(rawResponseText);
      if (normalizedResponseText) {
        const responseSearchSpace = ` ${normalizedResponseText} `;
        const runTextOrder = new Map<string, number>();
        citedBrands.forEach((brand: any) => {
          const key = resolveBrandKey(
            brand?.brandKey || brand?.brandDisplay || "",
            Boolean(brand?.isMain),
          );
          if (!key) return;
          if (selectedBrandKeys && !selectedBrandKeys.has(key)) return;

          const hasMentionedPage = Array.isArray(brand?.citedPages)
            ? brand.citedPages.some(
                (p: any) =>
                  p?.isMentioned &&
                  (!p?.sourceType || p.sourceType === "mention"),
              )
            : false;
          const hasBrandMention = hasMentionedPage;
          if (!hasBrandMention) return;

          const candidates = new Set<string>([
            key,
            brand?.brandKey || "",
            brand?.brandDisplay || "",
            options?.brandKeyLabels?.get(key) || "",
          ]);

          let bestIndex = Number.POSITIVE_INFINITY;
          candidates.forEach((candidate) => {
            const normalizedCandidate = normalizeSearchValue(candidate);
            if (!normalizedCandidate || normalizedCandidate.length < 3) return;
            const idx = responseSearchSpace.indexOf(` ${normalizedCandidate} `);
            if (idx >= 0 && idx < bestIndex) {
              bestIndex = idx;
            }
          });

          if (!Number.isFinite(bestIndex)) return;
          const existing = runTextOrder.get(key);
          if (existing === undefined || bestIndex < existing) {
            runTextOrder.set(key, bestIndex);
          }
        });

        const mainIndex = runTextOrder.get(canonicalMain);
        if (mainIndex !== undefined) {
          const rank =
            1 +
            Array.from(runTextOrder.values()).filter(
              (index) => index < mainIndex,
            ).length;
          if (rank > 0) {
            return renderPosition(rank);
          }
        }
      }

      const mainMetricPositions = citedBrands
        .map((brand: any) => {
          const key = resolveBrandKey(
            brand?.brandKey || brand?.brandDisplay || "",
            Boolean(brand?.isMain),
          );
          if (!key || key !== canonicalMain) return null;
          const hasMentionedPage = Array.isArray(brand?.citedPages)
            ? brand.citedPages.some(
                (p: any) =>
                  p?.isMentioned &&
                  (!p?.sourceType || p.sourceType === "mention"),
              )
            : false;
          if (!hasMentionedPage) {
            return null;
          }
          const position = Number(brand?.position);
          return Number.isFinite(position) && position > 0 ? position : null;
        })
        .filter((position: number | null): position is number =>
          Number.isFinite(position as number),
        );

      if (mainMetricPositions.length) {
        const bestMainPosition = Math.min(...mainMetricPositions);
        return renderPosition(bestMainPosition);
      }

      const runPositions = new Map<string, number>();

      citedBrands.forEach((brand: any) => {
        const hasMentionedPage = Array.isArray(brand?.citedPages)
          ? brand.citedPages.some(
              (p: any) =>
                p?.isMentioned &&
                (!p?.sourceType || p.sourceType === "mention"),
            )
          : false;
        if (!hasMentionedPage) return;
        const position = Number(brand?.position);
        if (!Number.isFinite(position) || position <= 0) return;
        const key = resolveBrandKey(
          brand?.brandKey || brand?.brandDisplay || "",
          Boolean(brand?.isMain),
        );
        if (!key) return;
        if (selectedBrandKeys && !selectedBrandKeys.has(key)) return;
        const existing = runPositions.get(key);
        if (!existing || position < existing) {
          runPositions.set(key, position);
        }
      });

      if (!runPositions.size) {
        return <div className="flex items-center justify-center">-</div>;
      }

      const sorted = Array.from(runPositions.entries()).sort(
        (a, b) => a[1] - b[1],
      );
      const rankMap = new Map<string, number>();
      sorted.forEach(([key], index) => {
        rankMap.set(key, index + 1);
      });

      const rank = rankMap.get(canonicalMain);
      if (!rank) {
        return <div className="flex items-center justify-center">-</div>;
      }
      return renderPosition(rank);
    },
  },
  {
    accessorKey: "mentions",
    header: "Mentions",
    cell: ({ row }: { row: any }) => {
      const citedPages = Array.isArray(row?.original?.citedPages)
        ? row.original.citedPages
        : [];
      const domains: string[] = citedPages
        .filter(
          (page: {
            type?: string;
            isMentioned?: boolean;
            sourceType?: string | null;
          }) =>
            ["Competitor", "Owned"].includes(String(page?.type || "")) &&
            Boolean(page?.isMentioned) &&
            (!page?.sourceType || page.sourceType === "mention"),
        )
        .map(
          (page: { url?: string }) => canonicalizeDomain(page?.url || "") || "",
        )
        .filter(Boolean);
      const uniqueDomains: string[] = Array.from(new Set(domains));
      const visibleDomains = uniqueDomains.slice(0, 4);
      const remainingCount = uniqueDomains.length - visibleDomains.length;
      return (
        <div className="capitalize flex items-center">
          <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
            {!uniqueDomains.length ? null : (
              <>
                {visibleDomains.map((domain) => (
                  <Avatar key={domain} className="border bg-white">
                    <img
                      className="logo"
                      src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${domain}&size=64`}
                      alt={domain}
                    />
                  </Avatar>
                ))}
              </>
            )}
          </div>
          {remainingCount > 0 ? (
            <span className="ml-3 text-sm text-muted-foreground">
              +{remainingCount}
            </span>
          ) : null}
        </div>
      );
    },
  },
  {
    accessorKey: "mentioned",
    header: "Mentioned",
    cell: ({ row }: any) => {
      const isLoadingRow = Boolean(row?.original?.isLoadingRow);
      const rowStatus = String(row?.original?.status || "").toLowerCase();
      const isNotTriggeredRow =
        rowStatus === "not_triggered" || rowStatus === "never_run";
      const hasResponse = runHasResponseContent(row?.original || {});
      if (isLoadingRow || isNotTriggeredRow || !hasResponse) {
        return null;
      }
      const citedBrands = Array.isArray(row?.original?.citedBrands)
        ? row.original.citedBrands
        : [];
      const normalizeBrandKey = (value: string) =>
        String(value || "")
          .trim()
          .toLowerCase();
      const canonicalMain = normalizeBrandKey(mainBrandKey || "");
      const resolveBrandKey =
        options?.resolveBrandKey ||
        ((value?: string | null, isMain?: boolean) =>
          isMain && canonicalMain
            ? canonicalMain
            : normalizeBrandKey(value || ""));
      const hasMention = citedBrands.some((brand: any) => {
        const key = resolveBrandKey(
          brand?.brandKey || brand?.brandDisplay || "",
          Boolean(brand?.isMain),
        );
        if (!key || !canonicalMain || key !== canonicalMain) return false;
        const hasMentionedPage = Array.isArray(brand?.citedPages)
          ? brand.citedPages.some(
              (p: any) =>
                p?.isMentioned &&
                (!p?.sourceType || p.sourceType === "mention"),
            )
          : false;
        return hasMentionedPage;
      });
      return (
        <div className="capitalize flex justify-center">
          {hasMention ? (
            <Badge className="bg-[#D6F1DF]" variant="secondary">
              <LocalizedText id="legacy.yes.376p8q" />
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-[#FFDBDC] text-[#CE2C31]">
              <LocalizedText id="legacy.no.3hnac" />
            </Badge>
          )}
        </div>
      );
    },
  },
];

const renderSnapshotUpdatedLabel = (
  updatedAt?: string | Date | null,
  isPending?: boolean,
) => {
  if (isPending) return "Fetching page...";
  if (!updatedAt) return "-";
  return getTimeAgoLabel(updatedAt);
};

const formatSnapshotExactTimestamp = (
  updatedAt?: string | Date | null,
): string | null => {
  if (!updatedAt) return null;
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const renderSnapshotUpdatedValue = (
  updatedAt?: string | Date | null,
  isPending?: boolean,
  isStale?: boolean,
  staleSuffix = " (stale)",
) => {
  const exactTimestamp = isPending
    ? null
    : formatSnapshotExactTimestamp(updatedAt);
  const text = `${renderSnapshotUpdatedLabel(updatedAt, isPending)}${
    isStale && !isPending ? staleSuffix : ""
  }`;
  if (!exactTimestamp) {
    return <span>{text}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default">{text}</span>
      </TooltipTrigger>
      <TooltipContent className="bg-black text-white border border-black">
        {exactTimestamp}
      </TooltipContent>
    </Tooltip>
  );
};

const SNAPSHOT_REFRESH_POLL_INTERVAL_MS = 15_000;
const SNAPSHOT_REFRESH_POLL_MAX_ATTEMPTS = 8;

const normalizeSnapshotCompareUrl = (value?: string | null): string =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\/+$/, "");

const findSnapshotStateByUrl = (
  stats: Record<string, any> | null | undefined,
  targetUrl: string,
): Record<string, any> | null => {
  if (!stats || typeof stats !== "object") return null;
  const normalizedTarget = normalizeSnapshotCompareUrl(targetUrl);
  if (!normalizedTarget) return null;
  const entries = Object.entries(stats);
  for (const [url, entry] of entries) {
    if (normalizeSnapshotCompareUrl(url) === normalizedTarget) {
      return entry as Record<string, any>;
    }
  }
  return null;
};

const extractSnapshotStateFromVisibilityPayload = (
  data: any,
  targetUrl: string,
): Record<string, any> | null => {
  const citedState = findSnapshotStateByUrl(data?.citedPagesStats, targetUrl);
  if (citedState) return citedState;
  return findSnapshotStateByUrl(data?.mentionedPagesStats, targetUrl);
};

const SnapshotDetailsDialog = ({ row }: { row: any }) => {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const details = row?.original?.snapshotDetails ?? null;
  const youtubeDetails = row?.original?.youtubeDetails ?? null;
  const updatedAt = row?.original?.updatedAt ?? null;
  const isPending = Boolean(row?.original?.isSnapshotPending);
  const isStale = Boolean(row?.original?.isSnapshotStale);
  const brandId = String(row?.original?.brandId || "").trim();
  const pageUrl = String(row?.original?.url || "").trim();
  const initialManualRateLimited = Boolean(
    row?.original?.isManualRefreshRateLimited,
  );
  const initialManualAvailableAt =
    row?.original?.manualRefreshAvailableAt ?? null;
  const initialRefreshQueued = Boolean(row?.original?.isSnapshotPending);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isManualRateLimited, setIsManualRateLimited] = React.useState(
    initialManualRateLimited,
  );
  const [isRefreshQueued, setIsRefreshQueued] =
    React.useState(initialRefreshQueued);
  const [manualAvailableAt, setManualAvailableAt] = React.useState<
    string | null
  >(initialManualAvailableAt);
  const refreshPollTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const refreshPollAttemptsRef = React.useRef(0);
  const refreshPollSawPendingRef = React.useRef(false);
  const refreshBaselineUpdatedAtRef = React.useRef<number | null>(null);
  const headings = Array.isArray(details?.topHeadings)
    ? details.topHeadings
        .map((item: string) => normalizeSnapshotText(item))
        .filter(Boolean)
    : [];
  const youtubeFlags = Array.isArray(youtubeDetails?.youtubeFlags)
    ? youtubeDetails.youtubeFlags
        .map((item: unknown) => String(item || "").trim())
        .filter(Boolean)
    : [];
  const titleText = normalizeSnapshotText(
    details?.title || youtubeDetails?.title,
  );
  const metaDescriptionText = normalizeSnapshotText(details?.metaDescription);
  const h1Text = normalizeSnapshotText(details?.h1);
  const excerptText = normalizeSnapshotText(
    details?.excerpt || youtubeDetails?.description,
  );
  const displayUrl = String(
    details?.finalUrl ||
      youtubeDetails?.url ||
      youtubeDetails?.originalUrl ||
      pageUrl ||
      "",
  ).trim();
  const channelNameText = normalizeSnapshotText(youtubeDetails?.channelName);
  const videoIdText = normalizeSnapshotText(youtubeDetails?.videoId);
  const youtubeStatusText = normalizeSnapshotText(youtubeDetails?.status);
  const youtubeErrorReasonText = normalizeSnapshotText(
    youtubeDetails?.errorReason,
  );
  const publishedAt = String(youtubeDetails?.publishedAt || "").trim();
  const publishedAtText = publishedAt
    ? formatSnapshotExactTimestamp(publishedAt) ||
      new Date(publishedAt).toLocaleString()
    : "-";
  const hasYoutubeMetadata = Boolean(youtubeDetails);
  const hasH1 = Boolean(h1Text);
  const hasMetaDescription = Boolean(metaDescriptionText);
  const hasHeadings = headings.length > 0;
  const statusCode = Number(details?.statusCode);
  const refreshDisabled =
    isRefreshing || isManualRateLimited || isPending || isRefreshQueued;
  const refreshTooltip = isManualRateLimited
    ? "You can refresh this page once per day."
    : isPending || isRefreshQueued
      ? "Refresh is already queued."
      : "Refresh snapshot";

  const clearRefreshPollingTimer = React.useCallback(() => {
    if (refreshPollTimerRef.current) {
      clearTimeout(refreshPollTimerRef.current);
      refreshPollTimerRef.current = null;
    }
  }, []);

  const isVisibilityQueryForBrand = React.useCallback(
    (queryKey: unknown): boolean => {
      if (!Array.isArray(queryKey) || queryKey.length < 2) return false;
      return (
        String(queryKey[0] || "") === "visability" &&
        String(queryKey[1] || "") === brandId
      );
    },
    [brandId],
  );

  const readLatestSnapshotStateFromCache = React.useCallback((): Record<
    string,
    any
  > | null => {
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) => isVisibilityQueryForBrand(query.queryKey),
    });
    let latestState: Record<string, any> | null = null;
    let latestUpdatedAtMs = -1;
    queries.forEach((query) => {
      const stateForUrl = extractSnapshotStateFromVisibilityPayload(
        query.state.data,
        pageUrl,
      );
      if (!stateForUrl) return;
      const rawUpdatedAtMs = Number(
        new Date(String(stateForUrl?.updatedAt || "")).getTime(),
      );
      const updatedAtMs = Number.isFinite(rawUpdatedAtMs) ? rawUpdatedAtMs : -1;
      if (!latestState || updatedAtMs >= latestUpdatedAtMs) {
        latestState = stateForUrl;
        latestUpdatedAtMs = updatedAtMs;
      }
    });
    return latestState;
  }, [isVisibilityQueryForBrand, pageUrl, queryClient]);

  const pollSnapshotRefreshCompletion = React.useCallback(async () => {
    if (!brandId || !pageUrl || !isRefreshQueued) return;
    refreshPollAttemptsRef.current += 1;
    await queryClient.invalidateQueries({
      predicate: (query) => isVisibilityQueryForBrand(query.queryKey),
      refetchType: "active",
    });

    const stateForUrl = readLatestSnapshotStateFromCache();
    if (stateForUrl) {
      const pending = Boolean(stateForUrl?.isSnapshotPending);
      if (pending) {
        refreshPollSawPendingRef.current = true;
      }
      const rawUpdatedAtMs = Number(
        new Date(String(stateForUrl?.updatedAt || "")).getTime(),
      );
      const updatedAtMs = Number.isFinite(rawUpdatedAtMs)
        ? rawUpdatedAtMs
        : null;
      const baselineUpdatedAtMs = refreshBaselineUpdatedAtRef.current;
      const hasUpdatedAfterBaseline =
        updatedAtMs !== null &&
        (baselineUpdatedAtMs === null || updatedAtMs > baselineUpdatedAtMs);

      if (
        !pending &&
        (hasUpdatedAfterBaseline || refreshPollSawPendingRef.current)
      ) {
        setIsRefreshQueued(false);
        clearRefreshPollingTimer();
        return;
      }
    }

    if (refreshPollAttemptsRef.current >= SNAPSHOT_REFRESH_POLL_MAX_ATTEMPTS) {
      setIsRefreshQueued(false);
      clearRefreshPollingTimer();
      toast.message(
        "Snapshot refresh is still processing. Please check again in a minute.",
      );
      return;
    }

    refreshPollTimerRef.current = setTimeout(() => {
      void pollSnapshotRefreshCompletion();
    }, SNAPSHOT_REFRESH_POLL_INTERVAL_MS);
  }, [
    brandId,
    clearRefreshPollingTimer,
    isRefreshQueued,
    isVisibilityQueryForBrand,
    pageUrl,
    queryClient,
    readLatestSnapshotStateFromCache,
  ]);

  React.useEffect(() => {
    setIsManualRateLimited(Boolean(row?.original?.isManualRefreshRateLimited));
    setManualAvailableAt(row?.original?.manualRefreshAvailableAt ?? null);
  }, [
    row?.original?.isManualRefreshRateLimited,
    row?.original?.manualRefreshAvailableAt,
  ]);

  React.useEffect(() => {
    setIsRefreshQueued(Boolean(row?.original?.isSnapshotPending));
  }, [row?.original?.isSnapshotPending, row?.original?.url]);

  React.useEffect(() => {
    if (!isRefreshQueued || !brandId || !pageUrl) {
      clearRefreshPollingTimer();
      return;
    }
    clearRefreshPollingTimer();
    refreshPollAttemptsRef.current = 0;
    refreshPollSawPendingRef.current = false;
    refreshPollTimerRef.current = setTimeout(() => {
      void pollSnapshotRefreshCompletion();
    }, SNAPSHOT_REFRESH_POLL_INTERVAL_MS);
    return clearRefreshPollingTimer;
  }, [
    brandId,
    clearRefreshPollingTimer,
    isRefreshQueued,
    pageUrl,
    pollSnapshotRefreshCompletion,
  ]);

  React.useEffect(() => {
    return () => {
      clearRefreshPollingTimer();
    };
  }, [clearRefreshPollingTimer]);

  const handleManualRefresh = async () => {
    if (!brandId || !pageUrl || refreshDisabled) return;
    try {
      setIsRefreshing(true);
      refreshBaselineUpdatedAtRef.current = Number.isFinite(
        new Date(String(updatedAt || "")).getTime(),
      )
        ? new Date(String(updatedAt || "")).getTime()
        : null;
      refreshPollAttemptsRef.current = 0;
      refreshPollSawPendingRef.current = false;
      const response = await visabilityAPI.refreshCitedPageSnapshot({
        brandId,
        url: pageUrl,
      });
      if (response?.status === "invalid_url") {
        setIsRefreshQueued(false);
        toast.error(t("legacy.invalid.url.for.snapshot.refresh.1nd8aor"));
        return;
      }
      if (response?.status === "rate_limited") {
        setIsRefreshQueued(false);
        setIsManualRateLimited(true);
        setManualAvailableAt(response?.availableAt ?? null);
        toast.message("You can refresh this page once per day.");
        return;
      }
      if (response?.status === "queued") {
        if (response?.availableAt) {
          setIsManualRateLimited(true);
          setManualAvailableAt(response.availableAt);
        }
        if (response?.queued === false) {
          setIsRefreshQueued(true);
          toast.message("Refresh is already queued.");
          return;
        }
        if (response?.queued === true) {
          setIsRefreshQueued(true);
          if (response?.dispatchMode === "direct") {
            toast.success(t("legacy.snapshot.refresh.started.knxf2z"));
            return;
          }
          toast.success(t("legacy.snapshot.refresh.queued.le18fj"));
          return;
        }
        setIsRefreshQueued(false);
        toast.message("Refresh request accepted.");
        return;
      }
      setIsRefreshQueued(false);
      toast.error(t("legacy.failed.to.queue.snapshot.refresh.frab1v"));
    } catch {
      toast.error(t("legacy.failed.to.queue.snapshot.refresh.frab1v"));
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 px-2">
          <FileText className="h-3.5 w-3.5" />
          <LocalizedText id="legacy.details.1uhd7mv" />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[min(96vw,72rem)] max-w-[72rem] sm:max-w-[72rem] max-h-[88vh] overflow-hidden p-0">
        <DialogHeader className="px-6 pt-4 pb-3 border-b bg-muted/20">
          <DialogDescription className="sr-only">
            <LocalizedText id="legacy.snapshot.or.youtube.metadata.and.excerpt.for.the.ewlepu" />
          </DialogDescription>
          <DialogTitle className="text-left flex items-center justify-between gap-3 pr-10 min-h-8">
            <div className="flex items-center gap-3">
              <span className="text-xl font-semibold tracking-tight">
                <LocalizedText id="legacy.cited.url.details.1327svn" />
              </span>
              <span className="text-xs text-muted-foreground">
                <LocalizedRichText
                  id="citedUrl.updated"
                  values={{
                    value: renderSnapshotUpdatedValue(
                      updatedAt,
                      isPending,
                      isStale,
                      t("citedUrl.staleSnapshotSuffix"),
                    ),
                  }}
                />
              </span>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 gap-1.5 min-w-[108px] justify-center"
                      disabled={refreshDisabled}
                      onClick={handleManualRefresh}
                    >
                      {isRefreshing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      <LocalizedText id="legacy.refresh.lydtiw" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{refreshTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogTitle>
          <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-1">
            {isManualRateLimited && manualAvailableAt ? (
              <span>
                ·{" "}
                <LocalizedText
                  id="citedUrl.refreshAvailableAt"
                  values={{
                    time:
                      formatSnapshotExactTimestamp(manualAvailableAt) ||
                      new Date(manualAvailableAt).toLocaleString(),
                  }}
                />
              </span>
            ) : null}
          </div>
        </DialogHeader>
        <div className="px-6 py-5 max-h-[calc(88vh-156px)] overflow-y-auto">
          {isPending && !details && !hasYoutubeMetadata ? (
            <p className="text-sm text-muted-foreground">
              <LocalizedText id="legacy.fetching.page.1156jj8" />
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {displayUrl ? (
                <section className="rounded-lg border bg-background p-4 md:col-span-2">
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <p className="uppercase tracking-wide leading-none">URL</p>
                    {Number.isFinite(statusCode) ? (
                      <span className="leading-none">
                        <LocalizedText id="legacy.status.1002bm3" />{" "}
                        {statusCode}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1.5 flex items-start justify-between gap-3">
                    <a
                      href={
                        displayUrl.startsWith("http")
                          ? displayUrl
                          : `https://${displayUrl}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block min-w-0 text-sm font-medium leading-6 hover:underline break-all"
                    >
                      {displayUrl}
                    </a>
                  </div>
                </section>
              ) : null}
              <section className="rounded-lg border bg-background p-4">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  <LocalizedText id="legacy.title.3ro0p1" />
                </p>
                <p className="mt-1.5 text-sm leading-6 text-foreground">
                  {titleText || "-"}
                </p>
              </section>
              {details ? (
                <>
                  {!hasYoutubeMetadata || hasH1 ? (
                    <section className="rounded-lg border bg-background p-4">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        H1
                      </p>
                      <p className="mt-1.5 text-sm leading-6 text-foreground">
                        {h1Text || "-"}
                      </p>
                    </section>
                  ) : null}
                  {!hasYoutubeMetadata || hasMetaDescription ? (
                    <section className="rounded-lg border bg-background p-4 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        <LocalizedText id="legacy.meta.description.j3nam6" />
                      </p>
                      <p className="mt-1.5 text-sm leading-6 text-foreground">
                        {metaDescriptionText || "-"}
                      </p>
                    </section>
                  ) : null}
                  {!hasYoutubeMetadata || hasHeadings ? (
                    <section className="rounded-lg border bg-background p-4 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        <LocalizedText id="legacy.top.headings.q1ewz9" />
                      </p>
                      {headings.length ? (
                        <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-foreground">
                          {headings
                            .slice(0, 10)
                            .map((heading: string, index: number) => (
                              <li
                                key={`${heading}-${index}`}
                                className="flex items-start gap-2"
                              >
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#FF5E00] shrink-0" />
                                <span className="leading-6">{heading}</span>
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <p className="mt-1.5 text-sm text-muted-foreground">
                          -
                        </p>
                      )}
                    </section>
                  ) : null}
                </>
              ) : null}
              {hasYoutubeMetadata ? (
                <>
                  <section className="rounded-lg border bg-background p-4">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      <LocalizedText id="legacy.channel.1viqmh2" />
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-foreground">
                      {channelNameText || "-"}
                    </p>
                  </section>
                  <section className="rounded-lg border bg-background p-4">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      <LocalizedText id="legacy.published.66pvx9" />
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-foreground">
                      {publishedAtText}
                    </p>
                  </section>
                  <section className="rounded-lg border bg-background p-4">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      <LocalizedText id="legacy.video.id.7c0oah" />
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-foreground">
                      {videoIdText || "-"}
                    </p>
                  </section>
                  <section className="rounded-lg border bg-background p-4">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      <LocalizedText id="legacy.metadata.status.iy8470" />
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-foreground">
                      {youtubeStatusText || "-"}
                    </p>
                  </section>
                  {youtubeFlags.length ? (
                    <section className="rounded-lg border bg-background p-4 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        <LocalizedText id="legacy.flags.3fa49m" />
                      </p>
                      <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-foreground">
                        {youtubeFlags
                          .slice(0, 10)
                          .map((flag: string, index: number) => (
                            <li
                              key={`${flag}-${index}`}
                              className="flex items-start gap-2"
                            >
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#FF5E00] shrink-0" />
                              <span className="leading-6">{flag}</span>
                            </li>
                          ))}
                      </ul>
                    </section>
                  ) : null}
                  {youtubeErrorReasonText ? (
                    <section className="rounded-lg border bg-background p-4 md:col-span-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        <LocalizedText id="legacy.metadata.error.1kcrngw" />
                      </p>
                      <p className="mt-1.5 text-sm leading-6 text-foreground">
                        {youtubeErrorReasonText}
                      </p>
                    </section>
                  ) : null}
                </>
              ) : null}
              <section className="rounded-lg border bg-background p-4 md:col-span-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {details
                    ? "Excerpt"
                    : hasYoutubeMetadata
                      ? "Description"
                      : "Excerpt"}
                </p>
                <div className="mt-2 rounded-md p-3 max-h-[40vh] overflow-y-auto">
                  <p className="text-[13px] leading-6 text-foreground whitespace-pre-wrap break-words">
                    {excerptText || "-"}
                  </p>
                </div>
              </section>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const partialPromptsDetails: ColumnDef<PartialData>[] = [
  {
    accessorKey: "url",
    header: "URL",
    cell: ({ row }) => {
      const rawUrl = String(row.getValue("url") || "");
      const formattedUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `https://${rawUrl}`;
      return (
        <div className="flex items-center gap-2 max-w-[500px] truncate overflow-hidden whitespace-nowrap">
          <img
            className="w-[20px] h-[20px]"
            src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedUrl}&size=64`}
          />
          <a
            href={formattedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              capturePosthogEvent("cited_domain_clicked", {
                domain: extractDomain(rawUrl),
                prompt_id: row?.original?.promptId || null,
                platform: toPlatformValue(
                  row?.original?.platforms ??
                    row?.original?.providers ??
                    row?.original?.provider,
                ),
                domain_type: toDomainType(
                  row?.original?.domainType ?? row?.original?.type,
                ),
              });
            }}
            className="truncate hover:underline hover:underline-offset-2"
          >
            {rawUrl}
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: () => (
      <div className="flex items-center gap-2">
        <span>
          <LocalizedText id="legacy.type.yjwvwd" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      const type: string = row.getValue("type");
      const typeLabel = toUrlTypeLabel(type);
      return (
        <div className="capitalize">
          <Badge className={getUrlTypeBadgeClassName(type)} variant="outline">
            {typeLabel}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "used",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.used.yjrj1e" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {row.getValue("used")}
      </div>
    ),
  },
];

export const partialDomains: ColumnDef<PartialData>[] = [
  {
    accessorKey: "domain",
    header: "Domain",
    cell: ({ row }) => {
      const rawUrl = row.getValue("domain") as string;
      const formattedUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `https://${rawUrl}`;
      return (
        <div className="lowercase flex items-center gap-2 max-w-[300px] truncate overflow-hidden">
          <img
            className="w-[24px] h-[24px]"
            src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${row.getValue("domain")}&size=64`}
          ></img>
          <a
            href={formattedUrl}
            className="group flex items-center hover:underline hover:underline-offset-2"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              capturePosthogEvent("cited_domain_clicked", {
                domain: extractDomain(String(row.getValue("domain") || "")),
                prompt_id: row?.original?.promptId || null,
                platform: toPlatformValue(
                  row?.original?.platforms ??
                    row?.original?.providers ??
                    row?.original?.provider,
                ),
                domain_type: toDomainType(
                  row?.original?.domainType ?? row?.original?.type,
                ),
              });
            }}
          >
            {row.getValue("domain")}

            <p className="p-1 opacity-0 transition-opacity group-hover:opacity-100 rounded-md bg-[#F5F5F5]">
              <SquareArrowOutUpRight size={16} />
            </p>
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "typeDomain",
    header: () => (
      <div className="flex items-center gap-2">
        <span>
          <LocalizedText id="legacy.type.yjwvwd" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      const type: string = row.getValue("typeDomain");
      return (
        <div className="capitalize">
          <Badge
            className={getDomainTypeBadgeClassName(type)}
            variant="outline"
          >
            {type}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "usedPercents",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.used.yjrj1e" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {row.getValue("usedPercents")}
      </div>
    ),
  },
];

export const promptsDetailsCitationsColumns: ColumnDef<Domains>[] = [
  {
    accessorKey: "#",
    header: "#",
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1 font-semibold">
          {row.index + 1}
        </div>
      );
    },
  },
  {
    accessorKey: "url",
    header: "Url",
    cell: ({ row }) => {
      const rawUrl = String(row.getValue("url") || "");
      const formattedUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `https://${rawUrl}`;
      return (
        <div className="flex items-center gap-2 max-w-[500px] truncate overflow-hidden whitespace-nowrap">
          <img
            className="w-[20px] h-[20px]"
            src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedUrl}&size=64`}
          />
          <a
            href={formattedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              capturePosthogEvent("cited_domain_clicked", {
                domain: extractDomain(rawUrl),
                prompt_id: row?.original?.promptId || null,
                platform: toPlatformValue(
                  row?.original?.platforms ??
                    row?.original?.providers ??
                    row?.original?.provider,
                ),
                domain_type: toDomainType(
                  row?.original?.domainType ?? row?.original?.type,
                ),
              });
            }}
            className="truncate hover:underline hover:underline-offset-2"
          >
            {rawUrl}
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "used",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.used.yjrj1e" />
          <ChevronsUpDown />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {row.getValue("used")}
      </div>
    ),
  },
  {
    accessorKey: "platforms",
    header: () => <LocalizedText id="common.platforms" />,
    cell: ({ row }) => {
      const providers = normalizeProviders(
        row?.original?.platforms ??
          row?.original?.providers ??
          row?.original?.provider,
      );
      return <div className="capitalize">{renderProviderIcons(providers)}</div>;
    },
  },
  {
    accessorKey: "type",
    header: () => (
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-4 w-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <LocalizedText id="legacy.actionable.classification.of.the.cited.url.listicle.alternatives.1795aqx" />
          </TooltipContent>
        </Tooltip>
        <span>
          <LocalizedText id="legacy.type.yjwvwd" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      const type: string = row.getValue("type");
      const typeLabel = toUrlTypeLabel(type);
      return (
        <div className="capitalize">
          <Badge className={getUrlTypeBadgeClassName(type)} variant="outline">
            {typeLabel}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "mentions",
    header: "Mentions",
    cell: ({ row }) => {
      const brands = Array.isArray(row?.original?.mentionedBrands)
        ? row.original.mentionedBrands
        : [];
      type Brand = { id: string; url?: string; name?: string; key?: string };
      const uniqueBrands = (brands as Brand[]).filter(
        (brand: Brand, index: number, self: Brand[]) => {
          const url = (brand?.url || "").trim().toLowerCase();
          if (!url) {
            return true;
          }
          return (
            self.findIndex(
              (item) => (item?.url || "").trim().toLowerCase() === url,
            ) === index
          );
        },
      );
      return (
        <div className="capitalize">
          <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
            {!uniqueBrands.length
              ? null
              : uniqueBrands.slice(0, 7).map((brand: Brand) => {
                  const label = brand?.name || brand?.key || "";
                  const initials = getInitials(label) || "?";
                  const brandUrl = brand?.url || "";
                  const formattedUrl = brandUrl
                    ? brandUrl.startsWith("http")
                      ? brandUrl
                      : `https://${brandUrl}`
                    : "";
                  return (
                    <Avatar key={brand.id} className="border bg-white">
                      {formattedUrl ? (
                        <img
                          src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedUrl}&size=64`}
                          alt={label || "brand"}
                        />
                      ) : (
                        <AvatarFallback>{initials}</AvatarFallback>
                      )}
                    </Avatar>
                  );
                })}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "mentioned",
    header: "Mentioned",
    cell: ({ row }) => (
      <div className="capitalize flex justify-center">
        {row.getValue("mentioned") === true ? (
          <Badge className="bg-[#D6F1DF]" variant="secondary">
            <LocalizedText id="legacy.yes.376p8q" />
          </Badge>
        ) : (
          <Badge variant="secondary" className="bg-[#FFDBDC] text-[#CE2C31]">
            <LocalizedText id="legacy.no.3hnac" />
          </Badge>
        )}
      </div>
    ),
  },
  {
    accessorKey: "updatedAt",
    header: "Updated",
    cell: ({ row }) => {
      const updatedAt = row?.original?.updatedAt;
      const isPending = Boolean(row?.original?.isSnapshotPending);
      const isStale = Boolean(row?.original?.isSnapshotStale);
      return (
        <div className="text-xs text-muted-foreground">
          {renderSnapshotUpdatedValue(updatedAt, isPending, isStale)}
        </div>
      );
    },
  },
  {
    id: "details",
    header: "Details",
    cell: ({ row }) => <SnapshotDetailsDialog row={row} />,
  },
];

export const citedDomainsColumns: ColumnDef<Domains>[] = [
  {
    accessorKey: "#",
    header: "#",
    cell: ({ row }) => {
      const rowNumber = Number(row?.original?.rowNumber);
      return (
        <div className="flex items-center gap-1 font-semibold">
          {Number.isFinite(rowNumber) && rowNumber > 0
            ? rowNumber
            : row.index + 1}
        </div>
      );
    },
  },
  {
    accessorKey: "domain",
    header: "Domain",
    cell: ({ row }) => {
      const rawUrl = row.getValue("domain") as string;
      const formattedUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `https://${rawUrl}`;
      return (
        <div className="lowercase flex items-center gap-2 max-w-[300px] truncate overflow-hidden">
          <img
            className="w-[24px] h-[24px]"
            src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${row.getValue("domain")}&size=64`}
          ></img>
          <a
            href={formattedUrl}
            className="group flex items-center hover:underline hover:underline-offset-2"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              capturePosthogEvent("cited_domain_clicked", {
                domain: extractDomain(String(row.getValue("domain") || "")),
                prompt_id: row?.original?.promptId || null,
                platform: toPlatformValue(
                  row?.original?.platforms ??
                    row?.original?.providers ??
                    row?.original?.provider,
                ),
                domain_type: toDomainType(
                  row?.original?.domainType ?? row?.original?.type,
                ),
              });
            }}
          >
            {row.getValue("domain")}

            <p className="p-1 opacity-0 transition-opacity group-hover:opacity-100 rounded-md bg-[#F5F5F5]">
              <SquareArrowOutUpRight size={16} />
            </p>
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "type",
    header: () => (
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-4 w-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <LocalizedText id="legacy.category.of.the.cited.source.publisher.competitor.community.c7nvpi" />
          </TooltipContent>
        </Tooltip>
        <span>
          <LocalizedText id="legacy.type.yjwvwd" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      const rawType = String(row.getValue("type") || "");
      const domain =
        canonicalizeDomain(String(row?.original?.domain || "")) || "";
      const normalizedType = (() => {
        const value = rawType.trim().toLowerCase();
        if (
          domain === "youtube.com" ||
          domain.endsWith(".youtube.com") ||
          domain === "youtu.be"
        ) {
          return "Community";
        }
        if (value.includes("competitor")) return "Competitor";
        if (value.includes("publisher")) return "Publisher";
        if (
          value.includes("community") ||
          value.includes("social") ||
          value.includes("forum") ||
          value.includes("ugc")
        ) {
          return "Community";
        }
        if (value.includes("owned") || value.includes("owner")) return "Owned";
        return "Other";
      })();
      return (
        <div className="capitalize">
          <Badge
            className={`
                ${normalizedType === "Competitor" && "border-[#6E56CF] text-[#6E56CF]"}
                ${normalizedType === "Owned" && "border-[#FF5E00] text-[#FF5E00]"}
                ${normalizedType === "Publisher" && "border-[#3DB9CF] text-[#3DB9CF]"}
                ${normalizedType === "Community" && "border-[#FFC53D] text-[#FFC53D]"}
                ${normalizedType === "Other" && "border-[#E5E5E5] text-[#0A0A0A]"}
              `}
            variant="outline"
          >
            {normalizedType}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "used",
    header: ({ column }) => {
      return (
        <div
          className="flex justify-end gap-1 items-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <LocalizedText id="legacy.how.many.times.this.domain.appeared.as.a.daqv9o" />
              </TooltipContent>
            </Tooltip>
            <span>
              <LocalizedText id="legacy.used.yjrj1e" />
            </span>
          </div>
          <ChevronsUpDown size={16} />
        </div>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-end">{row.getValue("used")}%</div>
    ),
  },
];

export const citedPagesColumns: ColumnDef<any>[] = [
  {
    accessorKey: "#",
    header: "#",
    cell: ({ row }) => {
      const rowNumber = Number(row?.original?.rowNumber);
      return (
        <div className="flex items-center gap-1 font-semibold">
          {Number.isFinite(rowNumber) && rowNumber > 0
            ? rowNumber
            : row.index + 1}
        </div>
      );
    },
  },
  {
    accessorKey: "url",
    header: "URL",
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1">
          <img
            className="w-[24px] h-[24px]"
            src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${row.getValue("url")}&size=64`}
          ></img>
          <a
            href={row.getValue("url")}
            className="group flex items-center"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              capturePosthogEvent("cited_domain_clicked", {
                domain: extractDomain(String(row.getValue("url") || "")),
                prompt_id: row?.original?.promptId || null,
                platform: toPlatformValue(
                  row?.original?.platforms ??
                    row?.original?.providers ??
                    row?.original?.provider,
                ),
                domain_type: toDomainType(
                  row?.original?.domainType ?? row?.original?.type,
                ),
              });
            }}
          >
            <p className="max-w-[300px] hover:underline hover:underline-offset-2 truncate overflow-hidden text-ellipsis whitespace-nowrap">
              {row.getValue("url")}
            </p>
            <p className="opacity-0 transition-opacity group-hover:opacity-100 p-1 rounded-md bg-[#F5F5F5]">
              <SquareArrowOutUpRight size={16} />
            </p>
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "used",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.used.yjrj1e" />
          <ChevronsUpDown />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {row.getValue("used")}
      </div>
    ),
  },
  {
    accessorKey: "platforms",
    header: () => <LocalizedText id="common.platforms" />,
    cell: ({ row }) => {
      const providers = normalizeProviders(
        row?.original?.platforms ??
          row?.original?.providers ??
          row?.original?.provider,
      );
      return <div className="capitalize">{renderProviderIcons(providers)}</div>;
    },
  },
  {
    accessorKey: "type",
    header: () => (
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-4 w-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <LocalizedText id="legacy.actionable.classification.of.the.cited.url.listicle.alternatives.1795aqx" />
          </TooltipContent>
        </Tooltip>
        <span>
          <LocalizedText id="legacy.type.yjwvwd" />
        </span>
      </div>
    ),
    cell: ({ row }) => {
      const type: string = row.getValue("type");
      return (
        <div className="capitalize">
          <Badge className={getUrlTypeBadgeClassName(type)} variant="outline">
            {type}
          </Badge>
        </div>
      );
    },
  },
  // {
  //   accessorKey: "mentionedBrands",
  //    header: () => (
  //     <div className="flex items-center gap-2">
  //       <Tooltip>
  //         <TooltipTrigger asChild>
  //           <Info className="h-4 w-4 text-muted-foreground" />
  //         </TooltipTrigger>
  //         <TooltipContent>
  //           <p>Just a mock tooltip</p>
  //         </TooltipContent>
  //       </Tooltip>
  //       <span>Mentions</span>
  //     </div>
  //   ),
  //   cell: ({ row }) => {
  //     const brands = row.original.mentionedBrands;

  //     return (
  //       <div className="flex flex-wrap gap-1">
  //         {brands.map((brand: { id: string; name: string }) => (
  //           <span
  //             key={brand.id}
  //             className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700"
  //           >
  //             {brand.name}
  //           </span>
  //         ))}
  //       </div>
  //     );
  //   },
  // },
  {
    accessorKey: "mentions",
    header: "Mentions",
    cell: ({ row }) => {
      const brands = Array.isArray(row?.original?.mentionedBrands)
        ? row.original.mentionedBrands
        : [];
      type Brand = { id: string; url?: string; name?: string; key?: string };
      const uniqueBrands = (brands as Brand[]).filter(
        (brand: Brand, index: number, self: Brand[]) => {
          const url = (brand?.url || "").trim().toLowerCase();
          if (!url) {
            return true;
          }
          return (
            self.findIndex(
              (item) => (item?.url || "").trim().toLowerCase() === url,
            ) === index
          );
        },
      );
      return (
        <div className="capitalize">
          <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
            {!uniqueBrands.length
              ? null
              : uniqueBrands.slice(0, 7).map((brand: Brand) => {
                  const label = brand?.name || brand?.key || "";
                  const initials = getInitials(label) || "?";
                  const brandUrl = brand?.url || "";
                  const formattedUrl = brandUrl
                    ? brandUrl.startsWith("http")
                      ? brandUrl
                      : `https://${brandUrl}`
                    : "";
                  return (
                    <Avatar key={brand.id} className="border bg-white">
                      {formattedUrl ? (
                        <img
                          src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${formattedUrl}&size=64`}
                          alt={label || "brand"}
                        />
                      ) : (
                        <AvatarFallback>{initials}</AvatarFallback>
                      )}
                    </Avatar>
                  );
                })}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "mentioned",
    header: () => (
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-4 w-4 text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent>
            <LocalizedText id="legacy.whether.your.brand.is.mentioned.on.this.page.1yf07xh" />
          </TooltipContent>
        </Tooltip>
        <span>
          <LocalizedText id="legacy.mentioned.134n50e" />
        </span>
      </div>
    ),
    cell: ({ row }) => (
      <div className="capitalize text-center">
        {row.getValue("mentioned") === true ? (
          <Badge className="bg-[#D6F1DF]" variant="secondary">
            <LocalizedText id="legacy.yes.376p8q" />
          </Badge>
        ) : (
          <div className="flex items-center justify-center text-center gap-2">
            <Badge className="bg-[#FFDBDC] text-[#CE2C31]" variant="secondary">
              <LocalizedText id="legacy.no.3hnac" />
            </Badge>
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: "updatedAt",
    header: "Updated",
    cell: ({ row }) => {
      const updatedAt = row?.original?.updatedAt;
      const isPending = Boolean(row?.original?.isSnapshotPending);
      const isStale = Boolean(row?.original?.isSnapshotStale);
      return (
        <div className="text-xs text-muted-foreground">
          {renderSnapshotUpdatedValue(updatedAt, isPending, isStale)}
        </div>
      );
    },
  },
  {
    id: "details",
    header: "Details",
    cell: ({ row }) => <SnapshotDetailsDialog row={row} />,
  },
];

export const getCitedPagesColumns = (
  onChangeSelect: (rowId: string, value: string) => void,
  selectedValues: Record<string, string>,
): ColumnDef<any>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value: boolean) =>
          table.toggleAllPageRowsSelected(!!value)
        }
        className="cursor-pointer
                bg-white
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0  
                data-[state=indeterminate]:bg-[#ff5e00] data-[state=indeterminate]:border-0 data-[state=indeterminate]:ring-0 data-[state=indeterminate]:focus:ring-0 data-[state=indeterminate]:focus-visible:ring-0 data-[state=indeterminate]:text-white"
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        onClick={(e) => e.stopPropagation()}
        checked={row.getIsSelected()}
        className="cursor-pointer
                data-[state=checked]:bg-[#ff5e00] data-[state=checked]:border-0 data-[state=checked]:ring-0 data-[state=checked]:focus:ring-0 data-[state=checked]:focus-visible:ring-0"
        onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "url",
    header: "URL",
    cell: ({ row }) => {
      const rawUrl = row.getValue("url") as string;
      const formattedUrl = rawUrl.startsWith("http")
        ? rawUrl
        : `https://${rawUrl}`;
      return (
        <div className="flex items-center gap-1">
          <a
            href={formattedUrl}
            className="group flex items-center"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              capturePosthogEvent("cited_domain_clicked", {
                domain: extractDomain(rawUrl),
                prompt_id: row?.original?.promptId || null,
                platform: toPlatformValue(
                  row?.original?.platforms ??
                    row?.original?.providers ??
                    row?.original?.provider,
                ),
                domain_type: toDomainType(
                  row?.original?.domainType ?? row?.original?.type,
                ),
              });
            }}
          >
            <div className="capitalize hover:underline hover:underline-offset-2 items-center gap-2 max-w-[300px] truncate overflow-hidden">
              {row.getValue("url")}
            </div>
            <p className="p-1 opacity-0 transition-opacity group-hover:opacity-100 rounded-md bg-[#F5F5F5]">
              <SquareArrowOutUpRight size={16} />
            </p>
          </a>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: () => <LocalizedText id="common.status" />,
    cell: ({ row }) => {
      const { t } = useLocale();
      const rowId = row.original?.id ?? row.original?.rank ?? row.id;
      const currentValue = selectedValues[rowId] || "";
      return (
        <div className="capitalize flex">
          <Select
            value={currentValue}
            onValueChange={(value) => onChangeSelect(rowId, value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue
                placeholder={t("legacy.select.status.192z8nt")}
                className="flex items-center gap-2"
              >
                {
                  statusOptions("#DC3E42", "#0588F0", "#2B9A66").find(
                    (opt) => opt.value === currentValue,
                  )?.icon
                }
                {
                  statusOptions("#DC3E42", "#0588F0", "#2B9A66").find(
                    (opt) => opt.value === currentValue,
                  )?.label
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {statusOptions("#DC3E42", "#0588F0", "#2B9A66")
                .filter((el) => el.value !== "default")
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      {option.icon}
                      {option.label}
                    </div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      );
    },
  },
  {
    accessorKey: "missedAnswers",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <LocalizedText id="legacy.missed.answers.jw1rtb" />
          <ChevronsUpDown />
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="lowercase flex justify-center">
        {row.getValue("missedAnswers")}
      </div>
    ),
  },

  {
    accessorKey: "mentions",
    header: "Mentions",
    cell: () => (
      <div className="capitalize">
        <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
          <Avatar>
            <AvatarImage
              src="https://www.gravatar.com/avatar/?d=mp&s=64"
              alt="Default avatar"
            />
            <AvatarFallback>CN</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarImage
              src="https://github.com/maxleiter.png"
              alt="@maxleiter"
            />
            <AvatarFallback>LR</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarImage
              src="https://github.com/evilrabbit.png"
              alt="@evilrabbit"
            />
            <AvatarFallback>ER</AvatarFallback>
          </Avatar>
        </div>
      </div>
    ),
  },
  {
    accessorKey: "mentioned",
    header: "Act",
    cell: ({ row }) => {
      return (
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline" className="cursor-pointer">
              <Sparkles />
              <LocalizedText id="legacy.draft.email.1i6vyss" />
            </Button>
          </SheetTrigger>
          <SheetContent className="sm:max-w-2xl p-4 overflow-auto">
            <div className="flex justify-between bg-white z-10">
              <div className="flex gap-2 items-center">
                <SheetClose asChild>
                  <Button variant="outline" className="cursor-pointer">
                    <ArrowLeft />
                    <LocalizedText id="legacy.back.yjmdam" />
                  </Button>
                </SheetClose>
                <Separator orientation="vertical" />
                <div className="flex items-center gap-2 font-semibold text-base text-[#0A0A0A] align-end">
                  <div className="h-[32px] w-[32px] bg-[#3B82F6] rounded-full flex items-center justify-center">
                    <Mail size={16} className="text-white"></Mail>
                  </div>{" "}
                  <LocalizedText id="legacy.draft.outreach.email.ots08v" />
                </div>
              </div>
            </div>
            <SheetHeader className="flex flex-row justify-between border rounded-lg  ">
              <SheetTitle className="sr-only">
                <LocalizedText id="legacy.execution.details.ieh02d" />
              </SheetTitle>
              <div className="flex w-full gap-3">
                <div className="flex items-center text-sm text-muted-foreground">
                  <LocalizedText id="legacy.source.1j8e6jc" />
                </div>
                <div className="flex text-sm gap-2">
                  <div className="flex items-center">
                    <LocalizedText id="legacy.logo.yjqxou" />
                  </div>
                  <div className="flex flex-col">
                    <div className="font-medium">
                      <LocalizedText id="legacy.description.r2fl5v" />
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {row.getValue("url")}
                    </div>
                  </div>
                </div>
              </div>
            </SheetHeader>
            <div className="flex flex-col border rounded-md h-full">
              <div className="grid grid-cols-[80px_1fr] p-4 border-b">
                <div className="font-medium text-sm">
                  <LocalizedText id="legacy.subject.s083mr" />
                </div>
                <textarea
                  defaultValue="Some subject"
                  className="text-sm text-muted-foreground"
                />
              </div>

              <div className="grid grid-cols-[80px_1fr] p-4">
                <div className="font-medium text-sm">
                  <LocalizedText id="legacy.body.3htwq7" />
                </div>
                <textarea
                  defaultValue="Some body"
                  className="text-sm text-muted-foreground h-full"
                />
              </div>
            </div>
            <SheetFooter className="flex flex-row justify-between">
              <Button className="grow cursor-pointer" variant="outline">
                {" "}
                <Copy />
                <LocalizedText id="legacy.copy.email.1eijn1o" />
              </Button>
              <Separator orientation="vertical" className="mx-3"></Separator>
              <Button className="grow cursor-pointer" variant="outline">
                <Mail />
                <LocalizedText id="legacy.open.in.gmail.ogxcxk" />
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      );
    },
  },
];

type ExportToCSVOptions = {
  emptyMessage?: string;
  successMessage?: string;
};

export function exportToCSV(
  data: any[],
  selectedRows: any[],
  filename = "prompts.csv",
  options: ExportToCSVOptions = {},
) {
  const emptyMessage =
    options.emptyMessage || "There is no data for export";
  const successMessage = options.successMessage || "CSV is downloaded";
  const exportData = selectedRows.length > 0 ? selectedRows : data;

  if (!exportData || exportData.length === 0) {
    toast.error(emptyMessage, {
      icon: (
        <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
          <TriangleAlert className="w-4 h-4 text-black" />
        </div>
      ),
      position: "bottom-right",
    });
    return;
  }

  const headers = Object.keys(data[0]).filter(
    (key) => !["id", "brand"].includes(key),
  );

  const csvRows = [
    headers.join(","),
    ...exportData.map((row) =>
      headers
        .map((field) => {
          let val = row[field];
          if (
            (field === "created_at" || field === "updated_at") &&
            typeof val === "string"
          ) {
            const date = new Date(val);
            val = date.toLocaleString("en-US", {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            });
          }

          if (field === "mentionedBrands") {
            val = val.map((brand: { name: string }) => brand.name).join(",");
          }

          if (val === undefined || val === null) val = "";
          val = String(val).replace(/"/g, '""');
          return `"${val}"`;
        })
        .join(","),
    ),
  ];

  const csvContent = csvRows.join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success(successMessage, {
    icon: (
      <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
        <Check className="w-4 h-4 text-black" />
      </div>
    ),
    position: "bottom-right",
  });
}

export const stepDescription = (brand?: Brand | null) => [
  {
    title: (
      <p className="text-white pr-[48px] text-2xl leading-8 tracking-normal font-medium font-sans mb-6 w-full ">
        Turn AI answers into a plan to get{" "}
        <span className="text-[#FF5E00]">
          <LocalizedText id="legacy.your.brand.8dvymn" />
        </span>{" "}
        cited
      </p>
    ),
    logo: <img className="mb-8" src={registrationIcon}></img>,

    description: (
      <p className="text-lg mb-12 max-w-md">
        <LocalizedText id="legacy.we.show.what.ai.cites.and.what.to.114h2v0" />
      </p>
    ),
    feature1: (
      <div className="text-base flex mb-6 gap-3 items-center">
        <div className="flex flwx-wrap">
          <div className="flex w-fit items-center gap-3">
            <Sparkles size={16}></Sparkles>
            <LocalizedText id="legacy.track.your.visibility.and.ranking.in.ai.answers.lao9z3" />
          </div>
          <span className="capitalize ml-2 grayscale invert">
            {renderProviderIcons(
              ["GPT", "PERP", "GOOGLE", "AI-MODE"],
              "size-6",
            )}
          </span>
        </div>
      </div>
    ),
    feature2: (
      <p className="text-base flex mb-6 gap-3 items-center">
        <MessageSquareQuoteIcon size={16}></MessageSquareQuoteIcon>{" "}
        <LocalizedText id="legacy.see.cited.pages.and.domains.behind.the.shortlist.jk7l84" />
      </p>
    ),
    feature3: (
      <p className="text-base flex mb-8 gap-3 items-center">
        <Waypoints size={16}></Waypoints>
        <LocalizedText id="legacy.get.tasks.and.a.strategy.for.each.buyer.mgav3w" />
      </p>
    ),
    additionalText:
      "You’ll get your first AI visibility strategy right after onboarding",
  },
  {
    title: (
      <p className="text-white text-2xl mb-6 w-full font-medium font-sans">
        <LocalizedText id="legacy.build.your.ai.visibility.baseline.5crf87" />
      </p>
    ),
    logo: <img className="mb-8" src={brandRegisterIcon}></img>,
    description: (
      <p className="text-lg mb-12 max-w-md">
        <LocalizedText id="legacy.well.use.this.to.tailor.your.prompts.clusters.up4php" />
      </p>
    ),
    feature1: (
      <div className="text-base flex mb-6 gap-3 items-center">
        <div className="flex flwx-wrap">
          <div className="flex w-fit items-center gap-3">
            <Globe size={16}></Globe>
            <LocalizedText id="legacy.your.website.gives.us.brand.context.yaff6r" />
          </div>
        </div>
      </div>
    ),
    feature2: (
      <p className="text-base flex mb-6 gap-3 items-center">
        <MapPin size={16}></MapPin>{" "}
        <LocalizedText id="legacy.country.determines.where.prompts.are.run.from.vyjgu8" />
      </p>
    ),
    feature3: (
      <p className="text-base flex mb-8 gap-3 items-center">
        <Focus size={16}></Focus>
        <LocalizedText id="legacy.optional.focus.helps.pick.better.topics.and.prompts.1hegpt6" />
      </p>
    ),
  },
  {
    title: (
      <p className="text-white text-2xl mb-6 w-full font-medium font-sans">
        <LocalizedText id="legacy.what.are.clusters.r251zz" />
      </p>
    ),
    logo: <img className="mb-8" src={clusterRegisterIcon}></img>,
    description: (
      <p className="text-lg mb-12 max-w-md">
        <LocalizedText id="legacy.clusters.group.many.similar.prompts.into.one.buying.327bwo" />
      </p>
    ),
    feature1: (
      <div className="text-base flex mb-6 gap-3 items-center">
        <div className="flex flwx-wrap">
          <div className="flex w-fit items-center gap-3">
            <CircleDotDashed size={16}></CircleDotDashed>
            <LocalizedText id="legacy.start.with.your.most.important.categories.xjci6b" />
          </div>
        </div>
      </div>
    ),
    feature2: (
      <p className="text-base flex mb-6 gap-3 items-center">
        <FolderTree size={16}></FolderTree>{" "}
        <LocalizedText id="legacy.each.cluster.generates.multiple.prompts.1mk448v" />
      </p>
    ),
    feature3: (
      <p className="text-base flex mb-8 gap-3 items-center">
        <MoveRight size={16}></MoveRight>
        <LocalizedText id="legacy.you.can.refine.this.later.iqhxrl" />
      </p>
    ),
  },
  {
    title: (
      <p className="text-white text-2xl mb-6 w-full font-medium font-sans">
        <LocalizedText id="legacy.choose.what.you.want.to.track.neeo5l" />
      </p>
    ),
    logo: <img className="mb-8" src={promptRegisterIcon}></img>,
    description: (
      <p className="text-lg mb-8 max-w-md">
        <LocalizedRichText
          id="onboardingPrompts.description"
          values={{
            brand: (
              <span className="inline-flex items-baseline mx-1">
                <span className="relative top-[6px] flex w-6 h-6 mr-1 items-center justify-center border rounded-md overflow-hidden">
                  <img
                    className="w-full h-full object-cover bg-white"
                    src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(brand?.url) || ""}&size=64`}
                    alt="logo"
                  />
                </span>
                <span className="text-[#FAFAFA] lowercase first-letter:uppercase">
                  {brand?.name}
                </span>
              </span>
            ),
          }}
        />
      </p>
    ),
    feature1: (
      <div className="text-base flex mb-6 gap-3 items-center">
        <div className="flex flwx-wrap">
          <div className="flex w-fit items-center gap-3">
            <CircleDotDashed size={16}></CircleDotDashed>
            <LocalizedText id="legacy.start.with.the.most.relevant.prompts.elk9gf" />
          </div>
        </div>
      </div>
    ),
    feature2: (
      <p className="text-base flex mb-6 gap-3 items-center">
        <CircleFadingPlus size={16}></CircleFadingPlus>{" "}
        <LocalizedText id="legacy.you.can.edit.or.add.more.anytime.1vaj8os" />
      </p>
    ),
    feature3: (
      <p className="text-base flex mb-8 gap-3 items-center">
        <CheckCheck size={16}></CheckCheck>
        <LocalizedText id="legacy.selected.prompts.will.run.automatically.every.day.14v4zmw" />
      </p>
    ),
  },
];
